import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import UserDashboardLayout from "../../layouts/UserDashboardLayout";
import { userAPI, achievementAPI } from "../../services/api";
import { usePreferences } from "../../context/PreferencesContext";
import { useCheckIn } from "../../context/CheckInContext";
import {
  FiUser, FiCreditCard, FiCheckCircle, FiMail, FiMapPin, FiCalendar, FiHeart, FiActivity, FiShield, FiLock, FiBell, FiGlobe, FiChevronDown, FiCheck, FiCamera, FiMessageCircle, FiAlertTriangle, FiTrash2, FiArrowRight, FiLoader, FiAlertCircle, FiChevronLeft, FiChevronRight, FiX, FiInfo, FiClock, FiDownload,
} from "react-icons/fi";
import Topbar from "../../components/userDashboard/Topbar";
import BrandLoader from "../../components/BrandLoader";
import { useNavigate, useSearchParams } from "react-router-dom";

const T = {
  orange: "#F29A0C", orange2: "#E97818",
  olive: "#6B8A5A",  olive2: "#8BA47A",
  dark: "#1F1F1F",   mid: "#555",
  muted: "#999",     light: "#CCC",
  border: "rgba(239,229,216,0.8)",
  red: "#E24B4A", green: "#16A34A",
};

const glass = {
  background: "rgba(255,255,255,0.82)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

const TABS = [
  { id: "personal",     label: "Personal",     icon: FiUser     },
  { id: "health",       label: "Health",       icon: FiHeart    },
  { id: "yoga",         label: "Yoga Profile", icon: FiActivity },
  { id: "preferences",  label: "Preferences",  icon: FiGlobe    },
  { id: "security",     label: "Security",     icon: FiShield   },
  { id: "subscription", label: "Subscription", icon: FiMessageCircle },
];

const YEARS_OF_PRACTICE_OPTIONS = [
  { value: 0, label: "0 years" },
  { value: 1, label: "1 year" },
  ...[2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20].map((n) => ({ value: n, label: `${n} years` })),
];

const parseYogaYears = (value) => {
  if (value === 0 || value === "0") return 0;
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const YOGA_TEXT_RE = /^[a-zA-Z0-9\s-]*$/;
const YOGA_NOTES_RE = /^[a-zA-Z0-9\s\-'.,]*$/;

const sanitizeYogaText = (value) => String(value ?? "").replace(/[^a-zA-Z0-9\s-]/g, "");
const sanitizeYogaNotes = (value) => String(value ?? "").replace(/[^a-zA-Z0-9\s\-'.,]/g, "");

const LETTERS_RE = /^[a-zA-Z\s]*$/;
const PHONE_RE = /^\+?[0-9]{7,15}$/;
const ADDRESS_RE = /^[a-zA-Z0-9\s\-,./#]*$/;
const NOTES_RE = /^[a-zA-Z0-9\s\-'.,!?()]*$/;
const BLOOD_GROUP_RE = /^(A|B|AB|O)[+-]$/;

const blockSignedNumberKeys = (e) => {
  if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
};

const sanitizeLetters = (value) => String(value ?? "").replace(/[^a-zA-Z\s]/g, "");
const sanitizePhone = (value) => {
  const raw = String(value ?? "");
  const plus = raw.trim().startsWith("+");
  const digits = raw.replace(/\D/g, "");
  return plus ? `+${digits}` : digits;
};
const sanitizeAddress = (value) => String(value ?? "").replace(/[^a-zA-Z0-9\s\-,./#]/g, "");
const sanitizeNotes = (value) => String(value ?? "").replace(/[^a-zA-Z0-9\s\-'.,!?()]/g, "");

const parseUnsignedNumber = (raw, { integer = false } = {}) => {
  let s = String(raw ?? "").replace(/[^\d.]/g, "");
  if (integer) s = s.replace(/\./g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  if (s === "" || s === ".") return "";
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return "";
  return integer ? String(Math.floor(n)) : s;
};

const validateBasicProfile = (basic = {}) => {
  const errors = {};
  const first = String(basic.firstName ?? "").trim();
  const last = String(basic.lastName ?? "").trim();
  if (!first) errors.firstName = "First name is required.";
  else if (!LETTERS_RE.test(first) || first.length > 50) errors.firstName = "Only letters and spaces are allowed (max 50).";
  if (!last) errors.lastName = "Last name is required.";
  else if (!LETTERS_RE.test(last) || last.length > 50) errors.lastName = "Only letters and spaces are allowed (max 50).";
  if (!String(basic.dateOfBirth ?? "").trim()) errors.dateOfBirth = "Date of birth is required.";
  else {
    const dob = new Date(basic.dateOfBirth);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (Number.isNaN(dob.getTime()) || dob > today) errors.dateOfBirth = "Date of birth cannot be in the future.";
    else if (dob.getFullYear() < 1920) errors.dateOfBirth = "Please enter a valid date of birth.";
  }
  if (!String(basic.gender ?? "").trim()) errors.gender = "Gender is required.";
  else if (!/^[a-zA-Z\s\-]+$/.test(basic.gender)) errors.gender = "Gender contains invalid characters.";
  const nationality = String(basic.nationality ?? "").trim();
  if (!nationality) errors.nationality = "Nationality is required.";
  else if (!LETTERS_RE.test(nationality) || nationality.length > 80) {
    errors.nationality = "Nationality can only contain letters and spaces.";
  }
  const country = String(basic.country ?? "").trim();
  if (!country) errors.country = "Country is required.";
  else if (!LETTERS_RE.test(country) || country.length > 80) {
    errors.country = "Country can only contain letters and spaces.";
  }
  if (basic.bio && (String(basic.bio).length > 300 || !NOTES_RE.test(basic.bio))) {
    errors.bio = "Bio can only contain letters, numbers, spaces, and basic punctuation (max 300).";
  }
  return errors;
};

const validateContactProfile = (contact = {}) => {
  const errors = {};
  const phone = String(contact.phone ?? "").replace(/\s/g, "");
  const whatsapp = String(contact.whatsapp ?? "").replace(/\s/g, "");
  const address = String(contact.address ?? "").trim();
  if (!phone) errors.phone = "Phone number is required.";
  else if (!PHONE_RE.test(phone)) errors.phone = "Enter 7-15 digits with an optional + (no minus or special characters).";
  if (!whatsapp) errors.whatsapp = "WhatsApp number is required.";
  else if (!PHONE_RE.test(whatsapp)) errors.whatsapp = "Enter 7-15 digits with an optional + (no minus or special characters).";
  if (!address) errors.address = "Address is required.";
  else if (address.length > 200 || !ADDRESS_RE.test(address)) {
    errors.address = "Address can only contain letters, numbers, spaces, and , . - / #.";
  }
  return errors;
};

const validateHealthProfile = (health = {}) => {
  const errors = {};
  const checkRange = (key, label, value, min, max, integer = false) => {
    if (value === "" || value === null || value === undefined) return;
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) {
      errors[key] = `${label} cannot be negative.`;
      return;
    }
    if (integer && !Number.isInteger(n)) {
      errors[key] = `${label} must be a whole number.`;
      return;
    }
    if (n < min || n > max) errors[key] = `${label} must be between ${min} and ${max}.`;
  };
  checkRange("weight", "Weight", health.weight, 20, 300);
  checkRange("height", "Height", health.height, 50, 250);
  checkRange("age", "Age", health.age, 1, 120, true);
  if (health.bloodGroup && !BLOOD_GROUP_RE.test(health.bloodGroup)) {
    errors.bloodGroup = "Select a valid blood group.";
  }
  if (health.dietaryPreference && !/^[a-zA-Z\s\-]+$/.test(health.dietaryPreference)) {
    errors.dietaryPreference = "Dietary preference can only contain letters, spaces, and hyphens.";
  }
  if (health.healthNotes && (String(health.healthNotes).length > 500 || !NOTES_RE.test(health.healthNotes))) {
    errors.healthNotes = "Health notes can only contain letters, numbers, spaces, and basic punctuation (max 500).";
  }
  return errors;
};

const validateYogaProfile = (yoga = {}) => {
  const errors = {};
  const checkText = (key, label, value, max = 80) => {
    const v = String(value ?? "").trim();
    if (!v) return;
    if (v.length > max) errors[key] = `${label} cannot exceed ${max} characters.`;
    else if (!YOGA_TEXT_RE.test(v)) errors[key] = `${label} can only contain letters, numbers, spaces, and hyphens.`;
  };

  checkText("level", "Experience level", yoga.level);
  checkText("style", "Primary style", yoga.style);
  checkText("goal", "Practice goal", yoga.goal);
  checkText("time", "Preferred time", yoga.time);
  checkText("sessions", "Sessions per week", yoga.sessions);

  const years = parseYogaYears(yoga.years);
  if (years !== null && (!Number.isInteger(years) || years < 0 || years > 80)) {
    errors.years = "Years of practice must be a whole number from 0 to 80.";
  }

  const notes = String(yoga.notes ?? "").trim();
  if (notes && (notes.length > 500 || !YOGA_NOTES_RE.test(notes))) {
    errors.notes = "Notes can only contain letters, numbers, spaces, and basic punctuation (max 500).";
  }

  return errors;
};

// ─── CUSTOM DROPDOWN COMPONENT ───────────────────────────────────────────────
function CustomSelect({ options = [], value, onChange, placeholder = "Select option", icon: Icon, allowCustomInput = false, error = false }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentVal = typeof value === "object" ? value?.value : value;
  const matchedOpt = options.find((o) => String(typeof o === "object" ? o.value : o) === String(currentVal));
  const selectedLabel = matchedOpt
    ? (typeof matchedOpt === "object" ? matchedOpt.label : matchedOpt)
    : (currentVal === 0 || currentVal === "0" || currentVal ? String(currentVal) : "");

  return (
    <div
      className="relative w-full"
      ref={containerRef}
      data-dropdown-open={open ? "true" : "false"}
      style={{ zIndex: open ? 9999 : 1 }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between rounded-[13px] px-4 py-2.5 text-[14px] outline-none cursor-pointer transition-all duration-200"
        style={{
          border: open ? `1.5px solid ${T.orange}` : `1.5px solid ${error ? T.red : T.border}`,
          background: "#FFFFFF",
          color: selectedLabel ? "#1F1F1F" : T.muted,
          boxShadow: open ? "0 0 0 4px rgba(242,154,12,0.12), 0 8px 24px rgba(0,0,0,0.06)" : error ? "0 0 0 4px rgba(226,75,74,0.12)" : "none",
          fontFamily: "'Inter',sans-serif",
        }}
      >
        <div className="flex items-center gap-2.5 truncate w-full pr-2">
          {Icon && <Icon size={14} style={{ color: T.orange }} />}
          <span className="truncate font-medium">{selectedLabel || placeholder}</span>
        </div>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <FiChevronDown size={16} style={{ color: open ? T.orange : T.muted }} />
        </motion.div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 4, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute left-0 right-0 z-[9999] rounded-[16px] py-1.5 shadow-2xl mt-1 overflow-hidden"
            style={{
              background: "#FFFFFF",
              border: `1.5px solid rgba(242,154,12,0.35)`,
              boxShadow: "0 24px 60px rgba(0,0,0,0.22), 0 0 0 1px rgba(255,255,255,1) inset",
            }}
          >
            {allowCustomInput && (
              <div className="p-2.5 border-b border-amber-100 bg-amber-50/60">
                <p className="text-[10px] font-black uppercase tracking-wider text-amber-800 mb-1">Custom Input / Type Below</p>
                <input
                  type="text"
                  placeholder="Type a custom value..."
                  value={currentVal === 0 || currentVal === "0" ? "0" : (currentVal ?? "")}
                  onChange={(e) => onChange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-amber-300 outline-none bg-white text-zinc-900 shadow-inner"
                />
              </div>
            )}
            <div className="max-h-[320px] overflow-y-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
              {options.map((opt) => {
                const label = typeof opt === "object" ? opt.label : opt;
                const val = typeof opt === "object" ? opt.value : opt;
                const isSelected = String(val) === String(currentVal);

                return (
                  <div
                    key={label}
                    onClick={() => {
                      onChange(val);
                      setOpen(false);
                    }}
                    className="px-4 py-2.5 text-[13px] font-semibold cursor-pointer transition-all duration-150 flex items-center justify-between hover:bg-amber-50/50"
                    style={{
                      background: isSelected ? "rgba(242,154,12,0.12)" : "transparent",
                      color: isSelected ? T.orange : "#1F1F1F",
                    }}
                  >
                    <span>{label}</span>
                    {isSelected && <FiCheck size={14} style={{ color: T.orange }} />}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── CUSTOM DATE PICKER COMPONENT ───────────────────────────────────────────
function CustomDatePicker({ value, onChange, error = false }) {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    return value ? new Date(value) : new Date(1995, 0, 15);
  });
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedDate = value ? new Date(value) : null;

  const currentYear = viewDate.getFullYear();
  const currentMonth = viewDate.getMonth();

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const years = Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - i);

  const handleSelectDay = (day) => {
    const yyyy = currentYear;
    const mm = String(currentMonth + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    const dateStr = `${yyyy}-${mm}-${dd}`;
    onChange(dateStr);
    setOpen(false);
  };

  const formattedDisplay = selectedDate
    ? selectedDate.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "Select Date of Birth";

  return (
    <div
      className="relative w-full"
      ref={containerRef}
      data-dropdown-open={open ? "true" : "false"}
      style={{ zIndex: open ? 9999 : 1 }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between rounded-[13px] px-4 py-2.5 text-[14px] outline-none cursor-pointer transition-all duration-200"
        style={{
          border: open ? `1.5px solid ${T.orange}` : `1.5px solid ${error ? T.red : T.border}`,
          background: "rgba(255,255,255,0.92)",
          color: selectedDate ? "#1F1F1F" : T.muted,
          boxShadow: open ? "0 0 0 4px rgba(242,154,12,0.12)" : error ? "0 0 0 4px rgba(226,75,74,0.12)" : "none",
          fontFamily: "'Inter',sans-serif",
        }}
      >
        <div className="flex items-center gap-2.5">
          <FiCalendar size={15} style={{ color: T.orange }} />
          <span className="font-medium">{formattedDisplay}</span>
        </div>
        <motion.div animate={{ rotate: open ? 180 : 0 }}>
          <FiChevronDown size={16} style={{ color: open ? T.orange : T.muted }} />
        </motion.div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 4, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 z-[150] w-[310px] p-4 rounded-[20px] shadow-2xl"
            style={{
              background: "rgba(255,255,255,0.97)",
              backdropFilter: "blur(24px)",
              border: "1.5px solid rgba(242,154,12,0.25)",
              boxShadow: "0 20px 50px rgba(0,0,0,0.14), 0 0 0 1px #fff inset",
            }}
          >
            {/* Header: Month & Year Controls */}
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={() => setViewDate(new Date(currentYear, currentMonth - 1, 1))}
                className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer hover:bg-black/5"
              >
                <FiChevronLeft size={16} style={{ color: T.orange }} />
              </button>

              <div className="flex items-center gap-1.5">
                <select
                  value={currentMonth}
                  onChange={(e) => setViewDate(new Date(currentYear, +e.target.value, 1))}
                  className="text-[12px] font-bold outline-none bg-transparent cursor-pointer"
                  style={{ color: "#1F1F1F" }}
                >
                  {months.map((m, i) => (
                    <option key={m} value={i}>{m}</option>
                  ))}
                </select>

                <select
                  value={currentYear}
                  onChange={(e) => setViewDate(new Date(+e.target.value, currentMonth, 1))}
                  className="text-[12px] font-bold outline-none bg-transparent cursor-pointer"
                  style={{ color: "#1F1F1F" }}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => setViewDate(new Date(currentYear, currentMonth + 1, 1))}
                className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer hover:bg-black/5"
              >
                <FiChevronRight size={16} style={{ color: T.orange }} />
              </button>
            </div>

            {/* Day Names Header */}
            <div className="grid grid-cols-7 text-center text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: T.muted }}>
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                <div key={d} className="py-1">{d}</div>
              ))}
            </div>

            {/* Calendar Days Grid */}
            <div className="grid grid-cols-7 gap-1 text-center text-[12px] font-semibold">
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}

              {Array.from({ length: daysInMonth }).map((_, i) => {
                const dayNum = i + 1;
                const isSelected =
                  selectedDate &&
                  selectedDate.getFullYear() === currentYear &&
                  selectedDate.getMonth() === currentMonth &&
                  selectedDate.getDate() === dayNum;

                return (
                  <motion.button
                    key={dayNum}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => handleSelectDay(dayNum)}
                    className="w-8 h-8 rounded-full flex items-center justify-center border-0 outline-none cursor-pointer mx-auto transition-colors"
                    style={{
                      background: isSelected ? `linear-gradient(135deg,${T.orange},${T.orange2})` : "transparent",
                      color: isSelected ? "#fff" : "#333",
                      boxShadow: isSelected ? "0 4px 12px rgba(242,154,12,0.4)" : "none",
                      fontFamily: "'Inter',sans-serif",
                    }}
                  >
                    {dayNum}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function FieldLabel({ children }) {
  return (
    <label className="block text-[10px] font-black uppercase tracking-[0.9px] mb-1.5"
      style={{ color:T.muted }}>{children}</label>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p className="text-[11px] font-semibold mt-1.5 leading-snug" style={{ color: T.red }}>
      {message}
    </p>
  );
}

function Input({ error, ...props }) {
  return (
    <input className="w-full rounded-[13px] px-4 py-2.5 text-[14px] text-[#1F1F1F] outline-none"
      style={{ border:`1.5px solid ${error ? T.red : T.border}`, background:"rgba(255,255,255,0.9)",
        fontFamily:"'Inter',sans-serif", transition:"border-color 0.2s, box-shadow 0.2s" }}
      onFocus={e=>{ e.target.style.borderColor=error ? T.red : T.orange; e.target.style.boxShadow=error ? "0 0 0 4px rgba(226,75,74,0.12)" : "0 0 0 4px rgba(242,154,12,0.1)"; }}
      onBlur={e=>{  e.target.style.borderColor=error ? T.red : T.border; e.target.style.boxShadow="none"; }}
      {...props} />
  );
}

function Textarea({ error, ...props }) {
  return (
    <textarea className="w-full rounded-[13px] px-4 py-3 text-[14px] text-[#1F1F1F] outline-none resize-none"
      style={{ border:`1.5px solid ${error ? T.red : T.border}`, background:"rgba(255,255,255,0.9)",
        fontFamily:"'Inter',sans-serif", lineHeight:1.7, transition:"border-color 0.2s" }}
      onFocus={e=>{ e.target.style.borderColor=error ? T.red : T.orange; e.target.style.boxShadow=error ? "0 0 0 4px rgba(226,75,74,0.12)" : "0 0 0 4px rgba(242,154,12,0.1)"; }}
      onBlur={e=>{  e.target.style.borderColor=error ? T.red : T.border; e.target.style.boxShadow="none"; }}
      {...props} />
  );
}

function SaveRow({ onSave, saving=false, accent=T.orange, accentGrad=`linear-gradient(135deg,${T.orange},${T.orange2})`, onDiscard }) {
  return (
    <div className="flex gap-3 pt-5 mt-5" style={{ borderTop:`1px solid ${T.border}` }}>
      <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }}
        onClick={onSave} disabled={saving}
        className="h-10 px-6 rounded-full text-white text-[13px] font-bold flex items-center gap-2"
        style={{ background:saving?"rgba(200,200,200,0.5)":accentGrad,
          boxShadow:saving?"none":`0 6px 20px ${accent}40`,
          border:"none", cursor:saving?"not-allowed":"pointer",
          fontFamily:"'Inter',sans-serif" }}>
        {saving
          ? <><motion.span animate={{ rotate:360 }} transition={{ duration:1,repeat:Infinity,ease:"linear" }}>
              <FiLoader size={13}/>
            </motion.span> Saving…</>
          : <><FiCheck size={13} /> Save Changes</>
        }
      </motion.button>
      <button onClick={onDiscard}
        className="h-10 px-5 rounded-full text-[13px] font-semibold"
        style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`,
          color:T.muted, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
        Discard
      </button>
    </div>
  );
}

// ─── TOGGLE ──────────────────────────────────────────────────────────────────
function Toggle({ isOn, onToggle }) {
  return (
    <motion.button whileTap={{ scale:0.95 }} onClick={onToggle}
      className="relative flex-shrink-0 rounded-full border-0 outline-none cursor-pointer"
      style={{ width:46, height:26,
        background:isOn?`linear-gradient(135deg,${T.orange},${T.orange2})`:"rgba(200,200,200,0.4)",
        boxShadow:isOn?`0 3px 10px rgba(242,154,12,0.4)`:"none",
        transition:"background 0.25s, box-shadow 0.25s" }}>
      <motion.div className="absolute top-[3px] w-5 h-5 rounded-full bg-white"
        animate={{ left:isOn?23:3 }} transition={{ type:"spring", stiffness:400, damping:28 }}
        style={{ boxShadow:"0 2px 6px rgba(0,0,0,0.18)" }} />
    </motion.button>
  );
}

function NotifRow({ title, sub, on, onChange }) {
  return (
    <div className="flex items-center justify-between py-3.5"
      style={{ borderBottom:`1px solid rgba(239,229,216,0.4)` }}>
      <div>
        <p className="text-[14px] font-bold text-[#1F1F1F]">{title}</p>
        <p className="text-[12px] mt-0.5" style={{ color:T.light }}>{sub}</p>
      </div>
      <Toggle isOn={!!on} onToggle={() => onChange && onChange(!on)}/>
    </div>
  );
}

// ─── ACCORDION ────────────────────────────────────────────────────────────────
function Accordion({ id, title, subtitle, iconBg, icon:Icon, defaultOpen=false, accent=T.orange, zIndex, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOlive = iconBg?.includes(T.olive.replace("#",""));

  return (
    <div className={`rounded-[18px] mb-3 ${open ? "overflow-visible relative [&:has([data-dropdown-open=true])]:!z-[1000]" : "overflow-hidden"}`}
      style={{ border:`1.5px solid ${open?(isOlive?"rgba(107,138,90,0.35)":"rgba(242,154,12,0.35)"):T.border}`,
        transition:"border-color 0.25s", zIndex: open ? (zIndex ?? 20) : 1 }}>
      <motion.div onClick={()=>setOpen(!open)}
        whileHover={{ background:open?(isOlive?"rgba(245,250,242,0.7)":"rgba(255,248,238,0.7)"):"rgba(255,255,255,0.95)" }}
        className="flex items-center justify-between px-6 py-[18px] cursor-pointer"
        style={{ background:open?(isOlive?"rgba(245,250,242,0.6)":"rgba(255,248,238,0.6)"):"rgba(255,255,255,0.7)", userSelect:"none" }}>
        <div className="flex items-center gap-4">
          <div className="w-[38px] h-[38px] rounded-[13px] flex items-center justify-center flex-shrink-0"
            style={{ background:iconBg, boxShadow:`0 5px 14px ${isOlive?"rgba(107,138,90,0.4)":"rgba(242,154,12,0.4)"}` }}>
            <Icon size={17} style={{ color:"#fff" }} />
          </div>
          <div>
            <p className="font-black text-[15px] text-[#1F1F1F]"
              style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.3px" }}>{title}</p>
            <p className="text-[11px] mt-0.5" style={{ color:T.light }}>{subtitle}</p>
          </div>
        </div>
        <motion.div animate={{ rotate:open?180:0 }} transition={{ duration:0.3, ease:[0.25,1,0.5,1] }}>
          <FiChevronDown size={18} style={{ color:open?(isOlive?T.olive:T.orange):T.light }} />
        </motion.div>
      </motion.div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="body"
            initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
            exit={{ height:0, opacity:0 }} transition={{ duration:0.4, ease:[0.25,1,0.5,1] }}
            style={{ overflow:"visible" }}>
            <div className="pf-acc-body px-6 pb-6 pt-5" style={{ borderTop:"1px solid rgba(239,229,216,0.4)" }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── CHANGE PASSWORD ──────────────────────────────────────────────────────────
function ChangePasswordSection({ showToast }) {
  const [form, setForm]   = useState({ currentPassword:"", newPassword:"", confirmNewPassword:"" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(p=>({...p,[name]:value}));
    if(errors[name]) setErrors(p=>({...p,[name]:""}));
  };

  const validate = () => {
    const e = {};
    if (!form.currentPassword)       e.currentPassword="Current password required";
    if (!form.newPassword)           e.newPassword="New password required";
    if (form.newPassword.length < 6) e.newPassword="Minimum 6 characters";
    if (form.newPassword !== form.confirmNewPassword) e.confirmNewPassword="Passwords do not match";
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if(Object.keys(e).length){ setErrors(e); return; }
    setSaving(true);
    try {
      await userAPI.changePassword(form);
      setForm({ currentPassword:"", newPassword:"", confirmNewPassword:"" });
      showToast("✓ Password changed successfully! Please log in again.");
      setTimeout(()=>{ localStorage.clear(); window.location.href="/login"; }, 2500);
    } catch(err){
      const msg = err.response?.data?.message || "Failed to change password.";
      if(msg.toLowerCase().includes("current")) setErrors({currentPassword:msg});
      else showToast(msg, "error");
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <FieldLabel>Current Password</FieldLabel>
        <Input type="password" name="currentPassword" value={form.currentPassword}
          onChange={handleChange} placeholder="Enter current password"/>
        {errors.currentPassword && <p className="text-[11px] mt-1" style={{color:T.red}}>{errors.currentPassword}</p>}
      </div>
      <div/>
      <div>
        <FieldLabel>New Password</FieldLabel>
        <Input type="password" name="newPassword" value={form.newPassword}
          onChange={handleChange} placeholder="Min. 6 characters"/>
        {errors.newPassword && <p className="text-[11px] mt-1" style={{color:T.red}}>{errors.newPassword}</p>}
      </div>
      <div>
        <FieldLabel>Confirm New Password</FieldLabel>
        <Input type="password" name="confirmNewPassword" value={form.confirmNewPassword}
          onChange={handleChange} placeholder="Repeat new password"/>
        {errors.confirmNewPassword && <p className="text-[11px] mt-1" style={{color:T.red}}>{errors.confirmNewPassword}</p>}
      </div>
      <div className="col-span-2">
        <SaveRow onSave={handleSave} saving={saving} onDiscard={()=>setForm({currentPassword:"",newPassword:"",confirmNewPassword:""})}/>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const { stats: checkInStats } = useCheckIn();

  const {
    language: prefLanguage,
    setLanguage: setPrefLanguage,
    updatePreferences
  } = usePreferences();

  const tabParam = searchParams.get("tab");
  const initialTab = (tabParam === "subscription" || tabParam === "subscriptions") ? "subscription" : "personal";
  const [activeTab, setActiveTab]   = useState(initialTab);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === "subscription" || t === "subscriptions") {
      setActiveTab("subscription");
    }
  }, [searchParams]);

  const [profile, setProfile]       = useState(null);
  const [summaryStats, setSummaryStats] = useState({ sessions: 0, streak: 0, badges: 0, points: "0", level: "Level 1 Yogi" });
  const [subscriptionData, setSubscriptionData] = useState({ activeSubscription: null, packages: [] });

  useEffect(() => {
    const targetPkgId = searchParams.get("packageId") || searchParams.get("selectedPackage") || sessionStorage.getItem("pending_subscription_package_id");
    if (targetPkgId && subscriptionData.packages && subscriptionData.packages.length > 0) {
      const match = subscriptionData.packages.find(p => String(p.id || p.Id) === String(targetPkgId));
      if (match) {
        setActiveTab("subscription");
        setSelectedCheckoutPkg(match);
        sessionStorage.removeItem("pending_subscription_package_id");
      }
    }
  }, [searchParams, subscriptionData.packages]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [saving, setSaving]         = useState({});
  const [forms, setForms]           = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast]           = useState(null);
  const [selectedCheckoutPkg, setSelectedCheckoutPkg] = useState(null);
  const [showManageBillingModal, setShowManageBillingModal] = useState(false);
  const [showSubHistoryModal, setShowSubHistoryModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deletePassword, setDeletePassword] = useState("");

  const [notifPrefs, setNotifPrefs] = useState({
    reminder24h: true,
    reminder1h: true,
    alert15m: true,
    checkInConfirm: true,
    cancellationAlert: true,
    badgeUnlocked: true,
    streakMilestone: true,
    goalCompleted: true,
    streakRisk: true,
    leaderboardRank: false,
    weeklyDietPlan: true,
    newVideos: false,
    platformAnnounce: true,
    expiryReminders: true,
    promotions: false,
  });

  const LANGUAGES = ["English","Hindi · हिन्दी","Spanish · Español","Arabic · عربي",
    "French · Français","German · Deutsch","Japanese · 日本語"];

  const showToast = (msg, type="success") => {
    setToast({msg,type}); setTimeout(()=>setToast(null), 3500);
  };

  const handleToggleNotifPref = async (key, val) => {
    const updated = { ...notifPrefs, [key]: val };
    setNotifPrefs(updated);
    try {
      await userAPI.updateProfile({
        notificationSettingsJson: JSON.stringify(updated)
      });
      showToast("✓ Notification setting updated & saved!");
    } catch (e) {
      showToast(e.message || "Failed to save notification preference.", "error");
    }
  };

  // ── Fetch on mount ──────────────────────────────────────────────────────────
  useEffect(() => { fetchProfile(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    setLoading(true); setError(null);
    setFieldErrors({});
    try {
      const [res, summaryRes, subRes] = await Promise.all([
        userAPI.getProfile(),
        achievementAPI.getSummary().catch(() => ({ data: { success: false } })),
        userAPI.getUserSubscription().catch(() => ({ data: { success: false } }))
      ]);

      const data = res.data.data;
      setProfile(data);
      window.dispatchEvent(new Event("profile-updated"));

      if (summaryRes.data?.success && summaryRes.data?.data) {
        const s = summaryRes.data.data;
        setSummaryStats({
          sessions: s.totalPractices ?? s.sessionsCompleted ?? s.totalClassesAttended ?? 0,
          streak: s.streak ?? s.streakDays ?? s.currentStreak ?? 0,
          badges: s.unlockedCount ?? s.badgesEarned ?? s.totalBadges ?? 0,
          points: (s.totalPoints ?? s.points ?? 0).toLocaleString(),
          level: s.level || "Level 1 Yogi"
        });
      }

      if (subRes.data?.success && subRes.data?.data) {
        const sData = subRes.data.data;
        setSubscriptionData({
          activeSubscription: sData.activeSubscription || sData.ActiveSubscription || null,
          packages: sData.packages || sData.Packages || []
        });
      }

      if (data.notificationSettingsJson) {
        try {
          const parsed = typeof data.notificationSettingsJson === "string" ? JSON.parse(data.notificationSettingsJson) : data.notificationSettingsJson;
          if (parsed && typeof parsed === "object") {
            setNotifPrefs(prev => ({ ...prev, ...parsed }));
          }
        } catch (e) {}
      }

      setForms({
        basic: {
          firstName: data.fullName?.split(" ")[0] || "",
          lastName:  data.fullName?.split(" ").slice(1).join(" ") || "",
          dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split("T")[0] : "",
          gender:    data.gender || "",
          nationality: data.nationality || "",
          country: data.country || "",
          bio: data.bio || "",
        },
        contact: {
          email: data.email,
          phone: data.phone || "",
          whatsapp: data.whatsApp || data.phone || "",
          address: data.address || "",
        },
        health: {
          weight: data.weight ?? "",
          height: data.height ?? "",
          age: data.age ?? "",
          bloodGroup: data.bloodGroup || "",
          dietaryPreference: data.dietaryPreference || "",
          selectedConditions: data.healthConditions ? (typeof data.healthConditions === "string" ? JSON.parse(data.healthConditions) : data.healthConditions) : ["None"],
          healthNotes: data.healthNotes || "",
        },
        yoga: {
          level: sanitizeYogaText(data.yogaLevel || ""),
          years: parseYogaYears(data.yogaYears),
          style: sanitizeYogaText(data.yogaStyle || ""),
          goal: sanitizeYogaText(data.yogaGoal || ""),
          time: sanitizeYogaText(data.yogaPreferredTime || ""),
          sessions: sanitizeYogaText(data.yogaSessionsPerWeek || ""),
          notes: sanitizeYogaNotes(data.yogaNotes || ""),
        },
        preferences: {
          timezone: data.timezone || "Asia/Kolkata (IST +5:30)",
          dateFormat: data.dateFormat || "DD/MM/YYYY",
          currency: data.currency || "INR — ₹",
          measurement: data.measurement || "Metric (kg, cm)",
          notifSettings: data.notificationSettingsJson ? (typeof data.notificationSettingsJson === "string" ? JSON.parse(data.notificationSettingsJson) : data.notificationSettingsJson) : { 0: true, 1: true, 2: true, 3: true, 4: false, 5: true }
        }
      });
      if (data.appLanguage) setPrefLanguage(data.appLanguage);
    } catch(err) {
      setError(err.response?.data?.message || "Failed to load profile.");
    } finally { setLoading(false); }
  };

  const handleFormChange = (section, field, value) => {
    setForms(p => ({ ...p, [section]: { ...p[section], [field]: value } }));
    setFieldErrors(p => (p[field] ? { ...p, [field]: "" } : p));
  };

  // ── Avatar Image Upload Handler ──────────────────────────────────────────────
  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      showToast("Please choose an image under 15MB.", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = async () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const size = Math.min(img.width, img.height);
          canvas.width = 300;
          canvas.height = 300;

          ctx.drawImage(
            img,
            (img.width - size) / 2,
            (img.height - size) / 2,
            size,
            size,
            0,
            0,
            300,
            300
          );

          const compressedBase64 = canvas.toDataURL("image/jpeg", 0.85);

          const payload = {
            fullName: profile.fullName,
            phone: profile.phone,
            gender: profile.gender,
            dateOfBirth: profile.dateOfBirth,
            address: profile.address,
            profileImageUrl: compressedBase64
          };
          const res = await userAPI.updateProfile(payload);
          const updatedData = res.data.data;
          setProfile(updatedData);

          const storedUser = JSON.parse(localStorage.getItem("divyayoga_user") || "{}");
          localStorage.setItem("divyayoga_user", JSON.stringify({ ...storedUser, ...updatedData }));

          window.dispatchEvent(new Event("profile-updated"));
          window.dispatchEvent(new Event("storage"));

          showToast("✓ Profile photo updated & saved successfully!");
        } catch (err) {
          showToast(err.response?.data?.message || "Failed to update profile photo.", "error");
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  // ── Save Section Handler   Sends Full Real Database Payload ──────────────────
  const saveSection = async (section) => {
    const f = forms;
    const sectionErrors =
      section === "basic" ? validateBasicProfile(f.basic)
      : section === "contact" ? validateContactProfile(f.contact)
      : section === "health" ? validateHealthProfile(f.health)
      : section === "yoga" ? validateYogaProfile(f.yoga)
      : {};

    const sectionKeys = {
      basic: ["firstName", "lastName", "dateOfBirth", "gender", "nationality", "country", "bio"],
      contact: ["phone", "whatsapp", "address"],
      health: ["weight", "height", "age", "bloodGroup", "dietaryPreference", "healthNotes"],
      yoga: ["level", "years", "style", "goal", "time", "sessions", "notes"],
    };
    setFieldErrors((prev) => {
      const next = { ...prev };
      (sectionKeys[section] || []).forEach((k) => { delete next[k]; });
      return { ...next, ...sectionErrors };
    });
    if (Object.keys(sectionErrors).length > 0) {
      showToast("Please fix the highlighted fields.", "error");
      return;
    }

    setSaving(p => ({ ...p, [section]: true }));
    try {
      const payload = {
        fullName: `${f.basic?.firstName || ""} ${f.basic?.lastName || ""}`.trim() || profile.fullName,
        phone: f.contact?.phone || profile.phone,
        gender: f.basic?.gender || profile.gender,
        dateOfBirth: f.basic?.dateOfBirth || null,
        address: f.contact?.address || profile.address,
        profileImageUrl: profile.profileImageUrl,

        // Extended Personal
        bio: f.basic?.bio,
        nationality: f.basic?.nationality,
        country: f.basic?.country,
        whatsApp: f.contact?.whatsapp,

        // Health Tab
        weight: f.health?.weight === "" || f.health?.weight === null || f.health?.weight === undefined ? null : Number(f.health.weight),
        height: f.health?.height === "" || f.health?.height === null || f.health?.height === undefined ? null : Number(f.health.height),
        age: f.health?.age === "" || f.health?.age === null || f.health?.age === undefined ? null : Number(f.health.age),
        bloodGroup: f.health?.bloodGroup,
        dietaryPreference: f.health?.dietaryPreference,
        healthConditions: JSON.stringify(f.health?.selectedConditions || ["None"]),
        healthNotes: f.health?.healthNotes,

        // Yoga Profile Tab
        yogaLevel: String(f.yoga?.level || "").trim(),
        yogaYears: parseYogaYears(f.yoga?.years),
        yogaStyle: String(f.yoga?.style || "").trim(),
        yogaGoal: String(f.yoga?.goal || "").trim(),
        yogaPreferredTime: String(f.yoga?.time || "").trim(),
        yogaSessionsPerWeek: String(f.yoga?.sessions || "").trim(),
        yogaNotes: String(f.yoga?.notes || "").trim(),

        // Preferences Tab
        appLanguage: prefLanguage,
        timezone: f.preferences?.timezone,
        dateFormat: f.preferences?.dateFormat,
        currency: f.preferences?.currency,
        measurement: f.preferences?.measurement,
        notificationSettingsJson: JSON.stringify(f.preferences?.notifSettings || {}),
      };

      const res = await userAPI.updateProfile(payload);
      setProfile(res.data.data);
      showToast(`✓ ${section.toUpperCase()} details saved successfully!`);
    } catch (err) {
      const data = err.response?.data;
      const apiErrors = data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        const keyMap = {
          FullName: "firstName", Phone: "phone", Gender: "gender", DateOfBirth: "dateOfBirth",
          Address: "address", Bio: "bio", Nationality: "nationality", Country: "country",
          WhatsApp: "whatsapp", Weight: "weight", Height: "height", Age: "age",
          BloodGroup: "bloodGroup", DietaryPreference: "dietaryPreference", HealthNotes: "healthNotes",
          YogaLevel: "level", YogaYears: "years", YogaStyle: "style", YogaGoal: "goal",
          YogaPreferredTime: "time", YogaSessionsPerWeek: "sessions", YogaNotes: "notes",
        };
        const mapped = {};
        Object.entries(apiErrors).forEach(([k, msgs]) => {
          const field = keyMap[k] || (k.charAt(0).toLowerCase() + k.slice(1));
          const msg = Array.isArray(msgs) ? msgs[0] : String(msgs || "");
          if (field && msg) mapped[field] = msg;
        });
        if (Object.keys(mapped).length) setFieldErrors((prev) => ({ ...prev, ...mapped }));
      }
      showToast(data?.message || data?.title || "Update failed.", "error");
    } finally {
      setSaving(p => ({ ...p, [section]: false }));
    }
  };

  const toggleHealthCondition = (c) => {
    setForms(prev => {
      const currentHealth = prev.health || {};
      let current = [...(currentHealth.selectedConditions || [])];
      if (c === "None") {
        current = ["None"];
      } else {
        current = current.filter(x => x !== "None");
        if (current.includes(c)) {
          current = current.filter(x => x !== c);
        } else {
          current.push(c);
        }
        if (current.length === 0) current = ["None"];
      }
      return {
        ...prev,
        health: { ...currentHealth, selectedConditions: current }
      };
    });
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== "DELETE") {
      showToast('Please type "DELETE" to confirm.', "error");
      return;
    }
    if (!deletePassword.trim()) {
      showToast("Please enter your password.", "error");
      return;
    }
    try {
      await userAPI.deleteAccountPermanent(deletePassword);
      showToast("Account deleted permanently.");
      setTimeout(() => {
        localStorage.clear();
        window.location.href = "/login";
      }, 2000);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete account.", "error");
    }
  };

  const handleDeactivateAccount = async () => {
    try {
      await userAPI.deactivateAccount();
      showToast("Account deactivated successfully.");
      setTimeout(() => {
        navigate("/account-deactivated");
      }, 1000);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to deactivate account.", "error");
    }
  };

  // ── Loading / error ─────────────────────────────────────────────────────────
  if (loading) return (
    <UserDashboardLayout>
      <Topbar />
      <BrandLoader 
        message="Loading Profile..." 
        subMessage="Retrieving your wellness account & settings" 
      />
    </UserDashboardLayout>
  );

  if (error) return (
    <UserDashboardLayout>
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <FiAlertCircle size={28} style={{ color:T.red }}/>
        <p className="text-[13px]" style={{ color:T.muted }}>{error}</p>
        <motion.button whileHover={{ y:-1 }} onClick={fetchProfile}
          className="h-10 px-6 rounded-full text-white text-[13px] font-bold border-none cursor-pointer"
          style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})` }}>
          Retry
        </motion.button>
      </div>
    </UserDashboardLayout>
  );

  const initials = profile.fullName ? profile.fullName.split(" ").slice(0,2).map(w=>w[0]).join("").toUpperCase() : "U";

  // 100% Dynamic Profile Completion calculation across Personal, Health, & Yoga fields (22 total fields)
  const basic = forms.basic || {};
  const contact = forms.contact || {};
  const health = forms.health || {};
  const yoga = forms.yoga || {};

  const completionFields = [
    // Personal Details (10)
    `${basic.firstName || ""} ${basic.lastName || ""}`.trim() || profile.fullName,
    contact.email || profile.email,
    contact.phone || profile.phone,
    contact.whatsapp || profile.whatsApp,
    contact.address || profile.address,
    basic.dateOfBirth || profile.dateOfBirth,
    basic.gender || profile.gender,
    basic.nationality || profile.nationality,
    basic.country || profile.country,
    basic.bio || profile.bio,

    // Health Details (6)
    health.weight ?? profile.weight,
    health.height ?? profile.height,
    health.age ?? profile.age,
    health.bloodGroup || profile.bloodGroup,
    health.dietaryPreference || profile.dietaryPreference,
    (health.selectedConditions && health.selectedConditions.length > 0) ? health.selectedConditions : profile.healthConditions,

    // Yoga Profile Details (6)
    yoga.level || profile.yogaLevel,
    yoga.years ?? profile.yogaYears,
    yoga.style || profile.yogaStyle,
    yoga.goal || profile.yogaGoal,
    yoga.time || profile.yogaPreferredTime,
    yoga.sessions || profile.yogaSessionsPerWeek,
  ];

  const filledFieldsCount = completionFields.filter(f => {
    if (f === null || f === undefined) return false;
    if (Array.isArray(f)) return f.length > 0;
    return String(f).trim().length > 0;
  }).length;

  const profileCompletionPercent = Math.round((filledFieldsCount / completionFields.length) * 100);

  // Calculate BMI dynamically
  const weightVal = Number(forms.health?.weight);
  const heightVal = Number(forms.health?.height);
  const hasBmi = Number.isFinite(weightVal) && weightVal > 0 && Number.isFinite(heightVal) && heightVal > 0;
  const bmiVal = hasBmi ? Math.round((weightVal / Math.pow(heightVal/100, 2)) * 10) / 10 : null;
  const bmiPct = hasBmi ? Math.min(Math.max((bmiVal-15)/25*100, 2), 96) : 2;
  const bmiLabel = !hasBmi ? "Add metrics" : bmiVal<18.5?"Underweight":bmiVal<25?"Normal Weight":bmiVal<30?"Overweight":"Obese";
  const bmiColor = !hasBmi ? T.muted : bmiVal<18.5?"#185FA5":bmiVal<25?T.olive:bmiVal<30?T.orange:"#E24B4A";

  // Subscription real calculations
  const activeSub = subscriptionData?.activeSubscription || subscriptionData?.ActiveSubscription;
  const packageList = (subscriptionData?.packages || subscriptionData?.Packages || []).filter(p => p.isActive !== false);

  const isFreeMember = !activeSub || activeSub.id === 0 || activeSub.packageType === "Free";
  const totalSubDays = (activeSub?.startDate && activeSub?.endDate)
    ? Math.max(1, Math.round((new Date(activeSub.endDate) - new Date(activeSub.startDate)) / (1000 * 60 * 60 * 24)))
    : 30;
  const remainingSubDays = activeSub?.remainingDays ?? 0;
  const subPercent = isFreeMember ? 0 : Math.min(100, Math.max(0, Math.round((remainingSubDays / totalSubDays) * 100)));

  return (
    <UserDashboardLayout>
      {/* Hidden Avatar File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleAvatarFileChange}
        accept="image/*"
        style={{ display: "none" }}
      />

      <div style={{ fontFamily:"'Inter',sans-serif" }}>

        {/* Ambient orbs */}
        <motion.div animate={{ y:[0,-20,0], x:[0,12,0] }} transition={{ duration:13, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[440px] h-[440px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.08),transparent 70%)", filter:"blur(80px)", top:-140, right:-90 }} />
        <motion.div animate={{ y:[0,14,0], x:[0,-10,0] }} transition={{ duration:16, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[360px] h-[360px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)", filter:"blur(80px)", bottom:-80, left:-80 }} />

        <div className="relative z-10 w-full min-w-0">
          <Topbar profile={profile}/>

          {/* ── HERO ── */}
          <motion.div initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}
            className="relative overflow-hidden p-5 sm:p-9"
            style={{ ...glass, borderBottomLeftRadius:0, borderBottomRightRadius:0 }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03] rounded-[inherit]"
              style={{ backgroundImage:"radial-gradient(circle,#000 1px,transparent 0)", backgroundSize:"22px 22px" }} />
            <div className="absolute top-0 right-0 pointer-events-none"
              style={{ width:260, height:260, background:"radial-gradient(circle,rgba(242,154,12,0.09),transparent)", filter:"blur(40px)" }} />

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-7 relative">
              {/* Avatar */}
              <div className="relative flex-shrink-0" onClick={handleAvatarClick}>
                <motion.div
                  animate={{ boxShadow:["0 0 0 0 rgba(242,154,12,0),0 16px 48px rgba(242,154,12,0.4)","0 0 0 7px rgba(242,154,12,0.12),0 16px 48px rgba(242,154,12,0.5)","0 0 0 0 rgba(242,154,12,0),0 16px 48px rgba(242,154,12,0.4)"] }}
                  transition={{ duration:4, repeat:Infinity }}
                  className="w-24 h-24 rounded-full flex items-center justify-center text-white font-black cursor-pointer overflow-hidden"
                  style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, fontSize:32, fontFamily:"'Poppins',sans-serif" }}>
                  {profile.profileImageUrl
                    ? <img src={profile.profileImageUrl} alt="" className="w-full h-full object-cover"/>
                    : <><div className="absolute inset-0" style={{ background:"linear-gradient(135deg,rgba(255,255,255,0.2),transparent)" }}/><span className="relative z-10">{initials}</span></>
                  }
                </motion.div>
                <div className="absolute bottom-0.5 right-0.5 w-7 h-7 rounded-full flex items-center justify-center cursor-pointer"
                  style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, border:"2.5px solid #FFF8EE", boxShadow:`0 3px 10px rgba(242,154,12,0.5)` }}>
                  <FiCamera size={11} style={{ color:"#fff" }} />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 w-full">
                <div className="flex items-center gap-2.5 flex-wrap mb-1.5">
                  <h1 className="font-black text-[#1F1F1F]"
                    style={{ fontFamily:"'Poppins',sans-serif", fontSize:26, letterSpacing:"-1px" }}>
                    {profile.fullName}
                  </h1>
                  <span className="text-[10px] font-black px-3 py-1 rounded-full text-white"
                    style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow:`0 3px 10px rgba(242,154,12,0.4)` }}>
                    {summaryStats.level || forms.yoga?.level || "Level 3 Yogi"}
                  </span>
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full"
                    style={{ background: isFreeMember ? "rgba(242,154,12,0.1)" : "rgba(107,138,90,0.1)",
                      border: isFreeMember ? "1px solid rgba(242,154,12,0.2)" : "1px solid rgba(107,138,90,0.2)",
                      color: isFreeMember ? T.orange : "#27500A" }}>
                    {isFreeMember ? "Free Member" : "Active Member"}
                  </span>
                </div>
                <div className="flex items-center gap-x-5 gap-y-1.5 flex-wrap mb-4">
                  {[
                    [FiMail, profile.email, T.orange],
                    [FiMapPin, profile.address || "Location not set", T.olive],
                    [FiCalendar, `Member since ${new Date(profile.createdAt).toLocaleDateString("en-IN",{month:"short",year:"numeric"})}`, T.orange],
                  ].map(([Icon, text, c]) => (
                    <span key={text} className="text-[12px] flex items-center gap-1.5 min-w-0 max-w-full break-all sm:break-normal" style={{ color:T.muted }}>
                      <Icon size={12} className="shrink-0" style={{ color:c }} />{text}
                    </span>
                  ))}
                </div>
                {/* 100% Dynamic Profile Completion Progress Bar */}
                <div className="flex items-center gap-3 max-w-sm">
                  <div className="flex-1">
                    <div className="flex justify-between text-[11px] mb-1.5">
                      <span className="font-semibold" style={{ color:T.mid }}>Profile completion</span>
                      <span className="font-black" style={{ color:T.orange }}>
                        {profileCompletionPercent}%
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background:"rgba(239,229,216,0.6)" }}>
                      <motion.div className="h-full rounded-full"
                        style={{ background:`linear-gradient(90deg,${T.orange},${T.orange2})` }}
                        initial={{ width:0 }}
                        animate={{ width:`${profileCompletionPercent}%` }}
                        transition={{ duration:1.2, ease:"easeOut" }} />
                    </div>
                  </div>
                  <span className="text-[11px] whitespace-nowrap font-medium" style={{ color:T.light }}>
                    {profileCompletionPercent === 100 ? "Complete!" : `${filledFieldsCount}/${completionFields.length} fields filled`}
                  </span>
                </div>
              </div>
            </div>

            {/* Stat strip — 100% Aligned Dashboard KPI Stats */}
            <div className="grid grid-cols-4 mt-7 pt-5" style={{ borderTop:`1px solid rgba(239,229,216,0.5)` }}>
              {[
                [checkInStats?.totalPractices ?? summaryStats.sessions, "Sessions"],
                [checkInStats?.streakDays ?? summaryStats.streak, "Day Streak"],
                [summaryStats.badges, "Badges"],
                [summaryStats.points, "Points"]
              ].map(([v,l]) => (
                <div key={l} className="text-center" style={{ borderRight:`1px solid rgba(239,229,216,0.4)` }}>
                  <p className="font-black text-[#1F1F1F]"
                    style={{ fontFamily:"'Poppins',sans-serif", fontSize:22, letterSpacing:"-1px" }}>{v}</p>
                  <p className="text-[10px] font-bold uppercase tracking-[0.7px] mt-1" style={{ color:T.light }}>{l}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── TAB BAR ── */}
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.15 }}
            className="flex items-center gap-0 overflow-x-auto"
            style={{ background:"rgba(255,255,255,0.82)", backdropFilter:"blur(32px)",
              borderLeft:"1px solid rgba(255,255,255,0.95)", borderRight:"1px solid rgba(255,255,255,0.95)",
              borderBottom:"1px solid rgba(255,255,255,0.95)", borderTop:`1px solid ${T.border}`,
              padding:"0 24px", scrollbarWidth:"none" }}>
            {TABS.map(tab => {
              const active = activeTab === tab.id;
              const Icon = tab.icon;
              return (
                <motion.button key={tab.id} onClick={()=>setActiveTab(tab.id)}
                  className="flex items-center gap-2 px-5 py-4 text-[13px] font-semibold whitespace-nowrap border-0 outline-none cursor-pointer"
                  style={{ background:"transparent", color:active?T.orange:T.muted,
                    fontWeight:active?700:600, fontFamily:"'Inter',sans-serif",
                    borderBottom:active?`2.5px solid ${T.orange}`:"2.5px solid transparent",
                    marginBottom:-1, transition:"color 0.22s, border-color 0.22s" }}>
                  <Icon size={15}/>{tab.label}
                </motion.button>
              );
            })}
          </motion.div>

          {/* ── CONTENT ── */}
          <div style={{ background:"rgba(255,255,255,0.82)", backdropFilter:"blur(32px)",
            WebkitBackdropFilter:"blur(32px)", border:"1px solid rgba(255,255,255,0.95)",
            borderTop:"none", borderRadius:"0 0 28px 28px",
            boxShadow:"0 20px 60px rgba(0,0,0,0.06)" }}>
            <AnimatePresence mode="wait">
              <motion.div key={activeTab}
                initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
                exit={{ opacity:0, y:-10 }} transition={{ duration:0.3 }}
                className="p-4 sm:p-9">

                {/* ── PERSONAL TAB ── */}
                {activeTab === "personal" && (
                  <>
                    <Accordion id="basic" title="Basic Details"
                      subtitle="Name, date of birth, gender, nationality, and bio"
                      iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`}
                      icon={FiUser} zIndex={30} defaultOpen>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div><FieldLabel>First Name <span className="text-red-500">*</span></FieldLabel>
                          <Input error={!!fieldErrors.firstName} value={forms.basic?.firstName||""} maxLength={50} onChange={e=>handleFormChange("basic","firstName",sanitizeLetters(e.target.value))}/>
                          <FieldError message={fieldErrors.firstName}/></div>
                        <div><FieldLabel>Last Name <span className="text-red-500">*</span></FieldLabel>
                          <Input error={!!fieldErrors.lastName} value={forms.basic?.lastName||""} maxLength={50} onChange={e=>handleFormChange("basic","lastName",sanitizeLetters(e.target.value))}/>
                          <FieldError message={fieldErrors.lastName}/></div>
                        <div>
                          <FieldLabel>Date of Birth <span className="text-red-500">*</span></FieldLabel>
                          <CustomDatePicker
                            error={!!fieldErrors.dateOfBirth}
                            value={forms.basic?.dateOfBirth || ""}
                            onChange={val => handleFormChange("basic", "dateOfBirth", val)}
                          />
                          <FieldError message={fieldErrors.dateOfBirth}/>
                        </div>
                        <div>
                          <FieldLabel>Gender <span className="text-red-500">*</span></FieldLabel>
                          <CustomSelect
                            error={!!fieldErrors.gender}
                            options={["Male", "Female", "Non-binary", "Prefer not to say"]}
                            value={forms.basic?.gender || ""}
                            onChange={val => handleFormChange("basic", "gender", val)}
                            placeholder="Select Gender"
                          />
                          <FieldError message={fieldErrors.gender}/>
                        </div>
                        <div><FieldLabel>Nationality <span className="text-red-500">*</span></FieldLabel>
                          <Input error={!!fieldErrors.nationality} value={forms.basic?.nationality||""} maxLength={80} onChange={e=>handleFormChange("basic","nationality",sanitizeLetters(e.target.value))}/>
                          <FieldError message={fieldErrors.nationality}/></div>
                        <div>
                          <FieldLabel>Country <span className="text-red-500">*</span></FieldLabel>
                          <CustomSelect
                            error={!!fieldErrors.country}
                            options={["India", "United States", "United Kingdom", "Canada", "Australia"]}
                            value={forms.basic?.country || ""}
                            onChange={val => handleFormChange("basic", "country", val)}
                            placeholder="Select Country"
                          />
                          <FieldError message={fieldErrors.country}/>
                        </div>
                      </div>
                      <div><FieldLabel>Short Bio</FieldLabel>
                        <Textarea error={!!fieldErrors.bio} rows={2} maxLength={300} value={forms.basic?.bio||""} onChange={e=>handleFormChange("basic","bio",sanitizeNotes(e.target.value))} placeholder="Tell us about your yoga journey…"/>
                        <FieldError message={fieldErrors.bio}/></div>
                      <SaveRow
                        onSave={()=>saveSection("basic")} saving={saving.basic}
                        onDiscard={fetchProfile}/>
                    </Accordion>

                    <Accordion id="contact" title="Contact Information"
                      subtitle={`Email · ${profile.phone || "No phone"} · ${profile.address || "No address"}`}
                      iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`} icon={FiMail} zIndex={20}>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <FieldLabel>Email Address <span className="text-red-500">*</span></FieldLabel>
                          <div className="flex items-center gap-2 rounded-[13px] px-4 py-2.5"
                            style={{ border:`1.5px solid ${T.border}`, background:"rgba(239,229,216,0.3)", color:T.muted, fontSize:14 }}>
                            <FiMail size={14} style={{ color:T.light }}/>
                            <span>{profile.email}</span>
                            <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full"
                              style={{ background:"rgba(200,200,200,0.2)", color:T.light }}>Locked</span>
                          </div>
                        </div>
                        <div><FieldLabel>Phone Number <span className="text-red-500">*</span></FieldLabel>
                          <Input error={!!fieldErrors.phone} value={forms.contact?.phone||""} maxLength={16} onChange={e=>handleFormChange("contact","phone",sanitizePhone(e.target.value))}/>
                          <FieldError message={fieldErrors.phone}/></div>
                        <div><FieldLabel>WhatsApp <span className="text-red-500">*</span></FieldLabel>
                          <Input error={!!fieldErrors.whatsapp} value={forms.contact?.whatsapp||""} maxLength={16} onChange={e=>handleFormChange("contact","whatsapp",sanitizePhone(e.target.value))}/>
                          <FieldError message={fieldErrors.whatsapp}/></div>
                        <div className="col-span-2"><FieldLabel>Address <span className="text-red-500">*</span></FieldLabel>
                          <Input error={!!fieldErrors.address} value={forms.contact?.address||""} maxLength={200} onChange={e=>handleFormChange("contact","address",sanitizeAddress(e.target.value))} placeholder="Your address"/>
                          <FieldError message={fieldErrors.address}/></div>
                      </div>
                      <SaveRow
                        onSave={()=>saveSection("contact")} saving={saving.contact}
                        accentGrad={`linear-gradient(135deg,${T.olive},${T.olive2})`} accent={T.olive}
                        onDiscard={fetchProfile}/>
                    </Accordion>
                  </>
                )}

                {/* ── HEALTH TAB ── */}
                {activeTab === "health" && (
                  <>
                    <Accordion id="bmi" title="Body Metrics & BMI"
                      subtitle="Weight, height, age, blood group, BMI index"
                      iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`}
                      icon={FiActivity} zIndex={30} defaultOpen>
                      
                      <div className="rounded-[16px] p-5 mb-5"
                        style={{ background:"rgba(245,250,242,0.7)", border:`1px solid rgba(107,138,90,0.15)` }}>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.8px] mb-1" style={{ color:T.olive }}>Your BMI</p>
                            <p className="font-black text-[38px] text-[#1F1F1F] leading-none"
                              style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-1.5px" }}>{hasBmi ? bmiVal : "—"}</p>
                          </div>
                          <span className="text-[12px] font-bold px-4 py-2 rounded-full"
                            style={{ background:`${bmiColor}18`, border:`1px solid ${bmiColor}35`, color:bmiColor }}>
                            {bmiLabel}
                          </span>
                        </div>
                        <div className="h-2.5 rounded-full relative mb-2"
                          style={{ background:"linear-gradient(90deg,#85B7EB 0%,#6B8A5A 28%,#F29A0C 55%,#E97818 72%,#E24B4A 100%)" }}>
                          <motion.div animate={{ left:`${bmiPct}%` }} transition={{ type:"spring", stiffness:200, damping:25 }}
                            className="absolute top-[-4px] w-[18px] h-[18px] rounded-full bg-white"
                            style={{ border:`3px solid ${T.orange}`, transform:"translateX(-50%)", boxShadow:"0 2px 8px rgba(0,0,0,0.2)" }} />
                        </div>
                        <div className="flex justify-between text-[10px] font-bold" style={{ color:T.light }}>
                          <span>Underweight</span><span>Normal</span><span>Overweight</span><span>Obese</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-3 mb-5">
                        {[[Number.isFinite(weightVal) && weightVal > 0 ? weightVal : "—","kg","Weight",T.orange],[Number.isFinite(heightVal) && heightVal > 0 ? heightVal : "—","cm","Height",T.olive],[forms.health?.age || "—","yrs","Age",T.orange],[forms.health?.bloodGroup || "—","","Blood Group",T.olive]].map(([val,unit,lbl,color]) => (
                          <div key={lbl} className="rounded-[16px] p-3.5 transition-all duration-250 cursor-default"
                            style={{ background:"rgba(255,255,255,0.85)", border:`1.5px solid ${T.border}` }}>
                            <p className="font-black text-[#1F1F1F] leading-none"
                              style={{ fontFamily:"'Poppins',sans-serif", fontSize:24, letterSpacing:"-0.8px" }}>
                              {val}{unit && <span className="text-[13px] font-medium" style={{ color:T.muted }}>{unit}</span>}
                            </p>
                            <p className="text-[10px] font-black uppercase tracking-[0.7px] mt-1" style={{ color }}>{lbl}</p>
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div><FieldLabel>Weight (kg)</FieldLabel>
                          <Input error={!!fieldErrors.weight} type="text" inputMode="decimal" value={forms.health?.weight ?? ""} onKeyDown={blockSignedNumberKeys} onChange={e=>handleFormChange("health", "weight", parseUnsignedNumber(e.target.value))} placeholder="e.g. 65"/>
                          <FieldError message={fieldErrors.weight}/>
                        </div>
                        <div><FieldLabel>Height (cm)</FieldLabel>
                          <Input error={!!fieldErrors.height} type="text" inputMode="decimal" value={forms.health?.height ?? ""} onKeyDown={blockSignedNumberKeys} onChange={e=>handleFormChange("health", "height", parseUnsignedNumber(e.target.value))} placeholder="e.g. 170"/>
                          <FieldError message={fieldErrors.height}/>
                        </div>
                        <div><FieldLabel>Age</FieldLabel>
                          <Input error={!!fieldErrors.age} type="text" inputMode="numeric" value={forms.health?.age ?? ""} onKeyDown={blockSignedNumberKeys} onChange={e=>handleFormChange("health", "age", parseUnsignedNumber(e.target.value, { integer: true }))} placeholder="e.g. 28"/>
                          <FieldError message={fieldErrors.age}/>
                        </div>
                        <div>
                          <FieldLabel>Blood Group</FieldLabel>
                          <CustomSelect
                            error={!!fieldErrors.bloodGroup}
                            options={["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"]}
                            value={forms.health?.bloodGroup || ""}
                            onChange={val => handleFormChange("health", "bloodGroup", val)}
                            placeholder="Select Blood Group"
                          />
                          <FieldError message={fieldErrors.bloodGroup}/>
                        </div>
                        <div className="col-span-2">
                          <FieldLabel>Dietary Preference</FieldLabel>
                          <CustomSelect
                            error={!!fieldErrors.dietaryPreference}
                            options={["Vegetarian", "Vegan", "Non-vegetarian", "Gluten-free"]}
                            value={forms.health?.dietaryPreference || ""}
                            onChange={val => handleFormChange("health", "dietaryPreference", val)}
                            placeholder="Select Dietary Preference"
                          />
                          <FieldError message={fieldErrors.dietaryPreference}/>
                        </div>
                      </div>

                      <SaveRow
                        onSave={() => saveSection("health")}
                        saving={saving.health}
                        accentGrad={`linear-gradient(135deg,${T.olive},${T.olive2})`} accent={T.olive}
                        onDiscard={fetchProfile}/>
                    </Accordion>

                    <Accordion id="conditions" title="Health Conditions"
                      subtitle="Conditions instructors should know about"
                      iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} icon={FiHeart} zIndex={20}>
                      <div className="flex flex-wrap gap-2 mb-5">
                        {["None","Hypertension","Diabetes","Back / Spine","Knee / Joint","Anxiety","Pregnancy"].map(c => {
                          const isSel = (forms.health?.selectedConditions || []).includes(c);
                          return (
                            <label key={c} onClick={() => toggleHealthCondition(c)} className="flex items-center gap-2 px-4 py-2.5 rounded-full cursor-pointer text-[13px] font-semibold transition-all select-none"
                              style={{
                                border: isSel ? `1.5px solid ${T.orange}` : `1.5px solid ${T.border}`,
                                background: isSel ? "rgba(242,154,12,0.12)" : "rgba(255,255,255,0.8)",
                                color: isSel ? T.orange : T.mid
                              }}>
                              <input type="checkbox" checked={isSel} onChange={() => {}} style={{ accentColor:T.orange }}/> {c}
                            </label>
                          );
                        })}
                      </div>
                      <div><FieldLabel>Notes for instructors</FieldLabel>
                        <Textarea error={!!fieldErrors.healthNotes} rows={2} maxLength={500} value={forms.health?.healthNotes || ""} onChange={e => handleFormChange("health", "healthNotes", sanitizeNotes(e.target.value))} placeholder="Any specific limitations, recent injuries..."/>
                        <FieldError message={fieldErrors.healthNotes}/></div>
                      <SaveRow onSave={() => saveSection("health")} saving={saving.health} onDiscard={fetchProfile}/>
                    </Accordion>
                  </>
                )}

                {/* ── YOGA PROFILE TAB ── */}
                {activeTab === "yoga" && (
                  <Accordion id="yoga" title="Practice Details"
                    subtitle="Level, style, goals, and experience"
                    iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`}
                    icon={FiActivity} defaultOpen>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <FieldLabel>Experience Level</FieldLabel>
                        <CustomSelect
                          error={!!fieldErrors.level}
                          options={["Beginner", "Intermediate", "Advanced", "Teacher"]}
                          value={forms.yoga?.level || ""}
                          onChange={val => handleFormChange("yoga", "level", sanitizeYogaText(val))}
                          placeholder="Select or type experience level"
                          allowCustomInput={true}
                        />
                        <FieldError message={fieldErrors.level}/>
                      </div>
                      <div>
                        <FieldLabel>Years of Practice</FieldLabel>
                        <CustomSelect
                          error={!!fieldErrors.years}
                          options={YEARS_OF_PRACTICE_OPTIONS}
                          value={forms.yoga?.years === 0 || forms.yoga?.years ? forms.yoga.years : ""}
                          onChange={(val) => {
                            const cleaned = String(val ?? "").replace(/[^\d]/g, "");
                            handleFormChange("yoga", "years", cleaned === "" ? "" : Number(cleaned));
                          }}
                          placeholder="Select or type years (0 is allowed)"
                          allowCustomInput={true}
                        />
                        <FieldError message={fieldErrors.years}/>
                      </div>
                      <div>
                        <FieldLabel>Primary Style</FieldLabel>
                        <CustomSelect
                          error={!!fieldErrors.style}
                          options={["Hatha", "Vinyasa", "Ashtanga", "Yin"]}
                          value={forms.yoga?.style || ""}
                          onChange={val => handleFormChange("yoga", "style", sanitizeYogaText(val))}
                          placeholder="Select or type primary style"
                          allowCustomInput={true}
                        />
                        <FieldError message={fieldErrors.style}/>
                      </div>
                      <div>
                        <FieldLabel>Practice Goal</FieldLabel>
                        <CustomSelect
                          error={!!fieldErrors.goal}
                          options={["Flexibility and Mindfulness", "Strength", "Stress Relief", "YTT"]}
                          value={forms.yoga?.goal || ""}
                          onChange={val => handleFormChange("yoga", "goal", sanitizeYogaText(val))}
                          placeholder="Select or type practice goal"
                          allowCustomInput={true}
                        />
                        <FieldError message={fieldErrors.goal}/>
                      </div>
                      <div>
                        <FieldLabel>Preferred Time</FieldLabel>
                        <CustomSelect
                          error={!!fieldErrors.time}
                          options={["Morning 6-8 AM", "Afternoon", "Evening"]}
                          value={forms.yoga?.time || ""}
                          onChange={val => handleFormChange("yoga", "time", sanitizeYogaText(val))}
                          placeholder="Select or type preferred time"
                          allowCustomInput={true}
                        />
                        <FieldError message={fieldErrors.time}/>
                      </div>
                      <div>
                        <FieldLabel>Sessions per Week</FieldLabel>
                        <CustomSelect
                          error={!!fieldErrors.sessions}
                          options={["1-2", "3-4", "5-6", "Daily"]}
                          value={forms.yoga?.sessions || ""}
                          onChange={val => handleFormChange("yoga", "sessions", sanitizeYogaText(val))}
                          placeholder="Select or type sessions per week"
                          allowCustomInput={true}
                        />
                        <FieldError message={fieldErrors.sessions}/>
                      </div>
                    </div>
                    <div><FieldLabel>Notes for instructors</FieldLabel>
                      <Textarea error={!!fieldErrors.notes} rows={2} maxLength={500} value={forms.yoga?.notes || ""} onChange={e => handleFormChange("yoga", "notes", sanitizeYogaNotes(e.target.value))} placeholder="Optional notes for instructors"/>
                      <FieldError message={fieldErrors.notes}/></div>
                    <SaveRow onSave={() => saveSection("yoga")} saving={saving.yoga} onDiscard={fetchProfile}/>
                  </Accordion>
                )}

                {/* ── PREFERENCES TAB — REAL i18n & FORMATTING ENGINE ── */}
                {activeTab === "preferences" && (
                  <>
                    {/* Language & Region Accordion commented out as requested */}
                    {/*
                    <Accordion id="lang" title="Language & Region"
                      subtitle="App language, timezone, currency, and measurement units"
                      iconBg="linear-gradient(135deg,#534AB7,#7F77DD)" icon={FiGlobe} defaultOpen>
                      <div className="mb-5">
                        <FieldLabel>App Language</FieldLabel>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {LANGUAGES.map(l => (
                            <motion.button key={l} whileTap={{ scale:0.95 }}
                              onClick={() => {
                                setPrefLanguage(l);
                                showToast(`✓ Language set to ${l}`);
                              }}
                              className="px-4 py-2 rounded-full text-[13px] font-bold border-0 outline-none cursor-pointer"
                              style={prefLanguage===l
                                ?{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, color:"#fff", boxShadow:`0 5px 14px rgba(242,154,12,0.38)`, fontFamily:"'Inter',sans-serif" }
                                :{ background:"rgba(255,255,255,0.82)", color:T.muted, border:`1.5px solid ${T.border}`, fontFamily:"'Inter',sans-serif" }
                              }>{l}</motion.button>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <FieldLabel>Timezone</FieldLabel>
                          <CustomSelect
                            options={["Asia/Kolkata (IST +5:30)", "America/New_York", "Europe/London"]}
                            value={forms.preferences?.timezone || "Asia/Kolkata (IST +5:30)"}
                            onChange={val => {
                              handleFormChange("preferences", "timezone", val);
                              updatePreferences({ timezone: val });
                            }}
                            placeholder="Select Timezone"
                          />
                        </div>
                        <div>
                          <FieldLabel>Date Format</FieldLabel>
                          <CustomSelect
                            options={["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"]}
                            value={forms.preferences?.dateFormat || "DD/MM/YYYY"}
                            onChange={val => {
                              handleFormChange("preferences", "dateFormat", val);
                              updatePreferences({ dateFormat: val });
                            }}
                            placeholder="Select Date Format"
                          />
                        </div>
                        <div>
                          <FieldLabel>Currency</FieldLabel>
                          <CustomSelect
                            options={["INR — ₹", "USD — $", "EUR — €"]}
                            value={forms.preferences?.currency || "INR — ₹"}
                            onChange={val => {
                              handleFormChange("preferences", "currency", val);
                              updatePreferences({ currency: val });
                            }}
                            placeholder="Select Currency"
                          />
                        </div>
                        <div>
                          <FieldLabel>Measurement</FieldLabel>
                          <CustomSelect
                            options={["Metric (kg, cm)", "Imperial (lbs, ft)"]}
                            value={forms.preferences?.measurement || "Metric (kg, cm)"}
                            onChange={val => {
                              handleFormChange("preferences", "measurement", val);
                              updatePreferences({ measurement: val });
                            }}
                            placeholder="Select Measurement"
                          />
                        </div>
                      </div>
                      <SaveRow onSave={() => {
                        saveSection("preferences");
                        updatePreferences(forms.preferences);
                      }} saving={saving.preferences} onDiscard={fetchProfile}/>
                    </Accordion>
                    */}

                    <Accordion id="notif" title="Notification Settings"
                      subtitle="Sessions, achievements, diet, and closed-browser push alerts"
                      iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`} icon={FiBell}>
                      
                      {/* Web Push Card inside Profile Preferences */}
                      <div className="rounded-[18px] p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                        style={{ background: "linear-gradient(135deg, rgba(242,154,12,0.1), rgba(107,138,90,0.1))", border: "1.5px solid rgba(242,154,12,0.25)" }}>
                        <div>
                          <p className="font-bold text-[14px] text-[#1F1F1F]">Browser & Closed-Tab Push Notifications</p>
                          <p className="text-[12px] text-[#777] mt-0.5">Receive native system popups on Windows, Android & Mac even when your browser tab is closed.</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <motion.button type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.96 }}
                            onClick={async () => {
                              try {
                                const { enableBrowserPushNotifications } = await import("../../services/pushNotification");
                                await enableBrowserPushNotifications();
                                showToast("✓ Browser Push Notifications Enabled!");
                              } catch (e) {
                                showToast(e.message || "Could not enable push notifications.", "error");
                              }
                            }}
                            className="h-9 px-5 rounded-full text-white text-[12px] font-bold flex items-center gap-1.5 cursor-pointer"
                            style={{ background: `linear-gradient(135deg, ${T.olive}, ${T.olive2})`, border: "none" }}>
                            <FiCheck size={13} /> Enable Web Push Notifications
                          </motion.button>
                        </div>
                      </div>

                      {/* Live Sessions */}
                      <p className="text-[10px] font-black uppercase tracking-wider text-[#999] mt-3 mb-2 px-1">Live Sessions & Reminders</p>
                      <NotifRow title="24-hour reminder" sub="Push + email the day before"
                        on={notifPrefs.reminder24h} onChange={(val) => handleToggleNotifPref("reminder24h", val)}/>
                      <NotifRow title="1-hour reminder" sub="Push notification before session"
                        on={notifPrefs.reminder1h} onChange={(val) => handleToggleNotifPref("reminder1h", val)}/>
                      <NotifRow title="15-minute alert — session starting" sub="Push notification"
                        on={notifPrefs.alert15m} onChange={(val) => handleToggleNotifPref("alert15m", val)}/>
                      <NotifRow title="Check-in confirmation" sub="In-app + email when you register"
                        on={notifPrefs.checkInConfirm} onChange={(val) => handleToggleNotifPref("checkInConfirm", val)}/>
                      <NotifRow title="Session cancellation alerts" sub="If cancelled or rescheduled"
                        on={notifPrefs.cancellationAlert} onChange={(val) => handleToggleNotifPref("cancellationAlert", val)}/>

                      {/* Progress & Achievements */}
                      <p className="text-[10px] font-black uppercase tracking-wider text-[#999] mt-5 mb-2 px-1">Progress & Achievements</p>
                      <NotifRow title="Badge unlocked" sub="Instant push + in-app popup"
                        on={notifPrefs.badgeUnlocked} onChange={(val) => handleToggleNotifPref("badgeUnlocked", val)}/>
                      <NotifRow title="Streak milestone reached" sub="7, 14, 30, 60, 100 day milestones"
                        on={notifPrefs.streakMilestone} onChange={(val) => handleToggleNotifPref("streakMilestone", val)}/>
                      <NotifRow title="Goal completed" sub="When you reach 100% on any goal"
                        on={notifPrefs.goalCompleted} onChange={(val) => handleToggleNotifPref("goalCompleted", val)}/>
                      <NotifRow title="Streak at risk" sub="Reminder if you haven't practised today"
                        on={notifPrefs.streakRisk} onChange={(val) => handleToggleNotifPref("streakRisk", val)}/>
                      <NotifRow title="Leaderboard rank change" sub="When your position shifts"
                        on={notifPrefs.leaderboardRank} onChange={(val) => handleToggleNotifPref("leaderboardRank", val)}/>

                      {/* Content & Plan Updates */}
                      <p className="text-[10px] font-black uppercase tracking-wider text-[#999] mt-5 mb-2 px-1">Content & Plan Updates</p>
                      <NotifRow title="Weekly diet plan published" sub="Every Monday morning"
                        on={notifPrefs.weeklyDietPlan} onChange={(val) => handleToggleNotifPref("weeklyDietPlan", val)}/>
                      <NotifRow title="New videos for your tier" sub="Email when added"
                        on={notifPrefs.newVideos} onChange={(val) => handleToggleNotifPref("newVideos", val)}/>
                      <NotifRow title="Platform announcements" sub="New features, maintenance windows"
                        on={notifPrefs.platformAnnounce} onChange={(val) => handleToggleNotifPref("platformAnnounce", val)}/>
                      <NotifRow title="Subscription expiry reminders" sub="7d, 3d, 1d before renewal"
                        on={notifPrefs.expiryReminders} onChange={(val) => handleToggleNotifPref("expiryReminders", val)}/>
                      <NotifRow title="Promotional offers" sub="Seasonal discounts"
                        on={notifPrefs.promotions} onChange={(val) => handleToggleNotifPref("promotions", val)}/>
                    </Accordion>
                  </>
                )}

                {/* ── SECURITY TAB ── */}
                {activeTab === "security" && (
                  <>
                    <Accordion id="pw" title="Change Password"
                      subtitle="Keep your account secure with a strong password"
                      iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} icon={FiLock} defaultOpen>
                      <ChangePasswordSection showToast={showToast}/>
                    </Accordion>

                    <Accordion id="danger" title="Deactivate Account"
                      subtitle="Deactivate or permanently delete account"
                      iconBg="linear-gradient(135deg,#E24B4A,#A32D2D)" icon={FiAlertTriangle}>
                      <div className="rounded-[16px] p-5 mb-3"
                        style={{ background:"rgba(252,235,235,0.4)", border:"1.5px solid rgba(226,75,74,0.2)" }}>
                        <p className="text-[14px] font-bold text-[#1F1F1F] mb-1.5">Deactivate Account</p>
                        <p className="text-[13px] leading-[1.65] mb-4" style={{ color:T.muted }}>
                          Your account will be paused. All data, badges, and subscription remain intact.
                        </p>
                        <button onClick={handleDeactivateAccount}
                          className="h-10 px-5 rounded-full text-[13px] font-bold flex items-center gap-2"
                          style={{ background:"transparent", border:"1.5px solid rgba(226,75,74,0.4)", color:"#E24B4A", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                          Deactivate My Account
                        </button>
                      </div>
                      <div className="rounded-[16px] p-5"
                        style={{ background:"rgba(252,235,235,0.6)", border:"2px solid rgba(226,75,74,0.3)" }}>
                        <p className="text-[14px] font-bold mb-1.5" style={{ color:"#E24B4A" }}>Delete Account Permanently</p>
                        <p className="text-[13px] leading-[1.65] mb-4" style={{ color:T.muted }}>
                          This will permanently erase all data. <strong style={{ color:"#E24B4A" }}>This cannot be undone.</strong>
                        </p>
                        <div className="mb-4 max-w-[240px]">
                          <FieldLabel>Type DELETE to confirm</FieldLabel>
                          <Input value={deleteConfirm} onChange={e=>setDeleteConfirm(e.target.value)}
                            placeholder="DELETE"
                            style={{ borderColor:"rgba(226,75,74,0.3)" }}/>
                        </div>
                        <div className="mb-4 max-w-[240px]">
                          <FieldLabel>Current Password</FieldLabel>
                          <Input
                            type="password"
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                            placeholder="Enter password"
                            style={{
                              borderColor: "rgba(226,75,74,0.3)"
                            }}
                          />
                        </div>
                        <button onClick={handleDeleteAccount}
                          className="h-10 px-5 rounded-full text-[13px] font-bold flex items-center gap-2"
                          style={{ background:"transparent", border:"1.5px solid rgba(226,75,74,0.6)", color:"#E24B4A",
                            cursor:deleteConfirm==="DELETE"?"pointer":"not-allowed",
                            opacity:deleteConfirm==="DELETE"?1:0.5,
                            fontFamily:"'Inter',sans-serif" }}>
                          <FiTrash2 size={13}/> Delete My Account
                        </button>
                      </div>
                    </Accordion>
                  </>
                )}

                {/* ── SUBSCRIPTION TAB   100% REAL BACKEND DATABASE DATA ── */}
                {activeTab === "subscription" && (
                  <Accordion id="plan" title="Current Plan"
                    subtitle={`${activeSub?.packageName || (isFreeMember ? "Free Member" : "Premium Plan")} · ${isFreeMember ? "Basic Access" : `${remainingSubDays} Days Remaining`}`}
                    iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`}
                    icon={FiMessageCircle} defaultOpen>
                    {/* Dynamic plan banner with real DB subscription */}
                    <div className="relative overflow-hidden rounded-[18px] p-6 mb-6"
                      style={{ background:"linear-gradient(145deg,#1F1F1F,#2D2D2D)", boxShadow:"0 16px 40px rgba(0,0,0,0.25)" }}>
                      <div className="absolute inset-0 opacity-[0.07]"
                        style={{ backgroundImage:"radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize:"14px 14px" }}/>
                      <div className="relative">
                        <p className="text-[10px] font-bold uppercase tracking-[1.5px] mb-1" style={{ color:"rgba(255,255,255,0.45)" }}>Active Subscription</p>
                        <p className="font-black text-white mb-3" style={{ fontFamily:"'Poppins',sans-serif", fontSize:22, letterSpacing:"-0.5px" }}>
                          {activeSub?.packageName || (isFreeMember ? "Free Member" : "Premium Plan")} <span className="text-[14px] font-semibold" style={{ color:"rgba(255,255,255,0.5)" }}>· {isFreeMember ? "Free Account" : "Active Member"}</span>
                        </p>
                        {/* Dynamic Subscription Progress Bar */}
                        <div className="h-1.5 rounded-full overflow-hidden mb-2" style={{ background:"rgba(255,255,255,0.12)", maxWidth:320 }}>
                          <motion.div
                            className="h-full rounded-full"
                            style={{ background:`linear-gradient(90deg,${T.orange},${T.orange2})` }}
                            initial={{ width: 0 }}
                            animate={{ width: `${subPercent}%` }}
                            transition={{ duration: 1, ease: "easeOut" }}
                          />
                        </div>
                        <p className="text-[12px]" style={{ color:"rgba(255,255,255,0.5)" }}>
                          {isFreeMember
                            ? "Enjoying basic access · Upgrade to unlock all live sessions & custom diet plans"
                            : `Valid until ${activeSub?.endDate ? new Date(activeSub.endDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "335 days remaining"} · ${remainingSubDays} Days Remaining`}
                        </p>
                      </div>
                    </div>

                    {/* Interactive Glassmorphic Tier Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
                      {(packageList.length > 0 ? packageList : [
                        { id: 1, name: "Free Trial", price: 0, discountPercentage: 0, packageType: "Free", durationMonths: 1, description: "Access to free beginner videos and community", featuresJson: "Beginner Yoga Classes, Community Forum Access" },
                        { id: 2, name: "Basic Plan", price: 599, discountPercentage: 0, packageType: "Basic", durationMonths: 1, description: "Access to standard classes and basic diet plans", featuresJson: "Standard Yoga Sessions, Basic Diet Plan, Community Access" },
                        { id: 3, name: "Standard Plan", price: 999, discountPercentage: 15, packageType: "Standard", durationMonths: 3, badgeTag: "15% OFF", description: "Access to all live sessions and custom diet plans", featuresJson: "All Live Sessions, Custom Diet Plans, HD Practice Library" },
                        { id: 4, name: "Premium Plan", price: 1499, discountPercentage: 50, packageType: "Premium", durationMonths: 12, badgeTag: "🔥 50% OFF", description: "Unlimited access to all live sessions & 1-on-1 consultation", featuresJson: "Unlimited Live Sessions, 1-on-1 Diet Consultation, YTT Access, Priority Support" },
                        { id: 5, name: "YTT Pro", price: 4999, discountPercentage: 20, packageType: "YTT", durationMonths: 12, badgeTag: "⭐ BEST VALUE", description: "Full Yoga Teacher Training Certification & Lifetime access", featuresJson: "YTT Certification Course, Lifetime Content Access, Master Workshops, 1-on-1 Mentorship" },
                      ]).map((pkg, idx) => {
                        const isActivePkg = activeSub
                          ? (activeSub.packageId === pkg.id || activeSub.packageType === pkg.packageType || (isFreeMember && pkg.packageType === "Free"))
                          : pkg.packageType === "Free";
                        const discPct = pkg.discountPercentage || 0;
                        const hasDiscount = discPct > 0;
                        const finalPrice = pkg.discountedPrice || Math.round(pkg.price * (1 - (discPct / 100)));
                        const isPopular = pkg.badgeTag?.includes("50%") || pkg.packageType === "Premium" || pkg.badgeTag?.includes("BEST");

                        const featuresList = (() => {
                          if (Array.isArray(pkg.features)) return pkg.features;
                          if (Array.isArray(pkg.featuresJson)) return pkg.featuresJson;
                          if (typeof pkg.featuresJson === "string" && pkg.featuresJson.trim()) {
                            try {
                              const parsed = JSON.parse(pkg.featuresJson);
                              if (Array.isArray(parsed) && parsed.length > 0) return parsed;
                            } catch {}
                            const split = pkg.featuresJson.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
                            if (split.length > 0) return split;
                          }
                          return ["Live Classes Access", "Custom Diet Plans", "HD Video Library"];
                        })();

                        return (
                          <motion.div
                            key={pkg.id || pkg.name || idx}
                            whileHover={{ y: -6, scale: 1.02 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            className="relative rounded-[24px] p-5 transition-all duration-300 flex flex-col justify-between overflow-hidden group"
                            style={{
                              background: isActivePkg
                                ? "linear-gradient(165deg, rgba(255,248,238,0.95) 0%, rgba(255,255,255,0.98) 100%)"
                                : isPopular
                                ? "linear-gradient(165deg, rgba(254,243,199,0.5) 0%, rgba(255,255,255,0.95) 100%)"
                                : "rgba(255,255,255,0.9)",
                              border: isActivePkg
                                ? `2.5px solid ${T.orange}`
                                : isPopular
                                ? `2px solid rgba(242,154,12,0.4)`
                                : `1.5px solid rgba(239,229,216,0.85)`,
                              boxShadow: isActivePkg
                                ? "0 20px 50px rgba(242,154,12,0.22), 0 0 0 1px rgba(242,154,12,0.3)"
                                : isPopular
                                ? "0 16px 40px rgba(242,154,12,0.12)"
                                : "0 10px 30px rgba(0,0,0,0.03)",
                            }}
                          >
                            {/* Ambient Glow Accent */}
                            <div
                              className="absolute -top-16 -right-16 w-32 h-32 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                              style={{ background: "radial-gradient(circle, rgba(242,154,12,0.25), transparent 70%)" }}
                            />

                            {/* Top Status / Badge Tag */}
                            {isActivePkg ? (
                              <div
                                className="absolute top-0 right-0 px-3 py-1 rounded-bl-[14px] text-[9px] font-black tracking-wider text-white uppercase shadow-sm"
                                style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})` }}
                              >
                                ACTIVE PLAN
                              </div>
                            ) : pkg.badgeTag ? (
                              <div
                                className="absolute top-0 right-0 px-2.5 py-1 rounded-bl-[14px] text-[8px] font-black tracking-wider text-white uppercase shadow-sm"
                                style={{ background: isPopular ? "linear-gradient(135deg, #E97818, #F29A0C)" : "linear-gradient(135deg, #16A34A, #15803D)" }}
                              >
                                {pkg.badgeTag}
                              </div>
                            ) : hasDiscount ? (
                              <div
                                className="absolute top-0 right-0 px-2.5 py-1 rounded-bl-[14px] text-[8px] font-black tracking-wider text-white uppercase shadow-sm"
                                style={{ background: "linear-gradient(135deg,#16A34A,#15803D)" }}
                              >
                                {discPct}% OFF
                              </div>
                            ) : null}

                            <div>
                              {/* Package Title */}
                              <div className="flex items-center gap-2 mb-2 pt-1">
                                <span
                                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{ background: isActivePkg ? T.orange : (isPopular ? T.orange2 : T.olive) }}
                                />
                                <p className="text-[13px] font-black tracking-tight text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>
                                  {pkg.name}
                                </p>
                              </div>

                              {/* Price */}
                              <div className="my-2.5 pb-2.5 border-b border-amber-900/10">
                                <div className="flex items-baseline gap-1 flex-wrap">
                                  <span className="font-black text-[#1F1F1F] leading-none" style={{ fontFamily: "'Poppins',sans-serif", fontSize: 24, letterSpacing: "-1px" }}>
                                    ₹{finalPrice?.toLocaleString()}
                                  </span>
                                  <span className="text-[10px] font-bold text-zinc-400">
                                    {pkg.durationInDays ? `/${pkg.durationInDays}d` : pkg.durationMonths ? `/${pkg.durationMonths}mo` : "/mo"}
                                  </span>
                                </div>
                                {hasDiscount && (
                                  <div className="flex items-center gap-1.5 mt-1">
                                    <span className="text-[10px] text-zinc-400 line-through font-medium">
                                      ₹{pkg.price?.toLocaleString()}
                                    </span>
                                    <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                                      Save ₹{(pkg.price - finalPrice).toLocaleString()}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Description */}
                              {pkg.description && (
                                <p className="text-[10px] text-zinc-500 mb-3 leading-relaxed">
                                  {pkg.description}
                                </p>
                              )}

                              {/* Included Features */}
                              <div className="space-y-1.5 mb-4">
                                {featuresList.map((feat, idx) => (
                                  <div key={idx} className="flex items-start gap-1.5 text-[10px] font-semibold text-zinc-700">
                                    <div
                                      className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                                      style={{ background: isActivePkg ? "rgba(242,154,12,0.15)" : "rgba(107,138,90,0.15)", color: isActivePkg ? T.orange : T.olive }}
                                    >
                                      <FiCheck size={9} />
                                    </div>
                                    <span className="leading-tight">{feat}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Button */}
                            <motion.button
                              type="button"
                              disabled
                              aria-disabled="true"
                              onClick={(e) => e.stopPropagation()}
                              className="w-full py-2.5 rounded-xl text-[11px] font-extrabold border-none cursor-not-allowed text-white shadow-md transition-all duration-200 mt-2"
                              style={{
                                background: isActivePkg
                                  ? `linear-gradient(135deg, ${T.orange}, ${T.orange2})`
                                  : isPopular
                                  ? `linear-gradient(135deg, #1F1F1F, #333333)`
                                  : `linear-gradient(135deg, ${T.olive}, ${T.olive2})`,
                                boxShadow: isActivePkg
                                  ? "0 6px 18px rgba(242,154,12,0.35)"
                                  : isPopular
                                  ? "0 6px 18px rgba(0,0,0,0.2)"
                                  : "0 5px 14px rgba(107,138,90,0.25)",
                                fontFamily: "'Inter',sans-serif",
                              }}
                            >
                              {isActivePkg ? "Active Plan" : "Choose Plan →"}
                            </motion.button>
                          </motion.div>
                        );
                      })}
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }}
                        onClick={() => setSelectedCheckoutPkg(packageList[0] || { name:"Premium Plan", price:1499, discountedPrice:749, discountPercentage:50 })}
                        className="h-11 px-6 rounded-full text-white text-[13px] font-bold flex items-center gap-2 cursor-pointer"
                        style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow:`0 8px 22px rgba(242,154,12,0.42)`, border:"none", fontFamily:"'Inter',sans-serif" }}>
                        Upgrade Plan <FiArrowRight size={14}/>
                      </motion.button>

                      <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }}
                        onClick={() => setShowManageBillingModal(true)}
                        className="h-11 px-5 rounded-full text-[13px] font-semibold flex items-center gap-2 cursor-pointer"
                        style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`, color:T.mid, fontFamily:"'Inter',sans-serif" }}>
                        <FiCreditCard size={14} style={{ color:T.orange }} /> Manage Billing
                      </motion.button>

                      <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }}
                        onClick={() => setShowSubHistoryModal(true)}
                        className="h-11 px-5 rounded-full text-[13px] font-semibold flex items-center gap-2 cursor-pointer"
                        style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`, color:T.mid, fontFamily:"'Inter',sans-serif" }}>
                        <FiClock size={14} style={{ color:T.olive }} /> Subscription History
                      </motion.button>
                    </div>
                  </Accordion>
                )}

                {/* Checkout Summary Modal */}
                {selectedCheckoutPkg && (
                  <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                    className="fixed inset-0 z-[200] flex items-center justify-center p-4"
                    style={{ background:"rgba(15,15,15,0.4)", backdropFilter:"blur(8px)" }}
                    onClick={() => setSelectedCheckoutPkg(null)}>
                    <motion.div initial={{ scale:0.95, opacity:0 }} animate={{ scale:1, opacity:1 }}
                      className="relative overflow-hidden rounded-[28px] p-7 max-w-[460px] w-full"
                      style={{ background:"#FFFDF8", boxShadow:"0 30px 90px rgba(0,0,0,0.22)", border:"1px solid rgba(255,255,255,0.9)" }}
                      onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="text-[10px] font-black uppercase tracking-[1px] text-[#F29A0C]">Checkout Summary</span>
                          <h3 className="text-[20px] font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>{selectedCheckoutPkg.name}</h3>
                        </div>
                        <button onClick={() => setSelectedCheckoutPkg(null)} className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center border-none cursor-pointer text-zinc-500">
                          <FiX size={16}/>
                        </button>
                      </div>

                      {/* Package details & features list */}
                      {selectedCheckoutPkg.description && (
                        <p className="text-[12px] text-zinc-600 mb-3">{selectedCheckoutPkg.description}</p>
                      )}

                      <div className="mb-4 space-y-1.5 bg-amber-500/5 p-3 rounded-[14px] border border-amber-500/10">
                        <span className="text-[10px] font-black uppercase text-amber-700 tracking-wider">Plan Highlights</span>
                        {(selectedCheckoutPkg.featuresJson
                          ? (selectedCheckoutPkg.featuresJson.startsWith("[") ? JSON.parse(selectedCheckoutPkg.featuresJson) : selectedCheckoutPkg.featuresJson.split(","))
                          : ["Unlimited Live Classes", "Custom Diet Plans", "YTT Support"]
                        ).map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-[11px] font-medium text-zinc-800">
                            <FiCheck size={12} className="text-emerald-600 shrink-0"/>
                            <span>{f.trim()}</span>
                          </div>
                        ))}
                      </div>

                      {/* Pricing Breakdown */}
                      <div className="rounded-[18px] p-4 mb-5" style={{ background:"rgba(255,244,232,0.6)", border:"1.5px solid rgba(242,154,12,0.2)" }}>
                        <div className="flex justify-between text-[13px] py-1 border-b border-zinc-200/60">
                          <span className="text-zinc-600">Original Plan Price</span>
                          <span className="font-bold text-zinc-900">₹{selectedCheckoutPkg.price?.toLocaleString()}</span>
                        </div>
                        {(selectedCheckoutPkg.discountPercentage || 0) > 0 && (
                          <div className="flex justify-between text-[13px] py-1 border-b border-zinc-200/60 text-emerald-600">
                            <span>Discount Applied ({selectedCheckoutPkg.discountPercentage}%)</span>
                            <span className="font-bold">-₹{(selectedCheckoutPkg.price - (selectedCheckoutPkg.discountedPrice || selectedCheckoutPkg.price)).toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-[16px] pt-3 font-black text-zinc-900">
                          <span>Total Amount</span>
                          <span style={{ color:"#F29A0C" }}>₹{(selectedCheckoutPkg.discountedPrice || selectedCheckoutPkg.price)?.toLocaleString()}</span>
                        </div>
                      </div>

                      <div className="rounded-[16px] p-4 bg-amber-500/10 border border-amber-500/20 mb-5 flex items-start gap-3">
                        <FiAlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5"/>
                        <div>
                          <h4 className="text-[13px] font-bold text-amber-900 mb-0.5">Online Payment Gateway Integration Status</h4>
                          <p className="text-[12px] text-amber-800 leading-relaxed">
                            Real Online Razorpay Payment Gateway is currently in test mode. Online payments will require configured API keys.
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2.5">
                        <button onClick={() => {
                          showToast("Payment Gateway Info: Real Razorpay online payment integration is pending live API credentials. Subscription not altered.", "info");
                        }}
                          className="w-full py-3.5 rounded-full text-amber-900 bg-amber-100 border border-amber-300 font-bold text-[13px] cursor-pointer hover:bg-amber-200 transition-all flex items-center justify-center gap-2">
                          <FiCreditCard size={15}/> Real Payment Gateway (Coming Soon)
                        </button>

                        <button type="button" disabled aria-disabled="true"
                          className="w-full py-3.5 rounded-full text-white font-bold text-[13px] border-none cursor-not-allowed shadow-md flex items-center justify-center gap-2"
                          style={{ background:"linear-gradient(135deg,#6B8A5A,#8BA47A)", fontFamily:"'Inter',sans-serif" }}>
                          <FiCheckCircle size={15}/> Simulate Test Sandbox Activation
                        </button>
                      </div>
                    </motion.div>
                  </motion.div>
                )}

                {/* Manage Billing Modal */}
                <AnimatePresence>
                  {showManageBillingModal && (
                    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
                      style={{ background:"rgba(15,15,15,0.45)", backdropFilter:"blur(8px)" }}
                      onClick={() => setShowManageBillingModal(false)}>
                      <motion.div initial={{ scale:0.95, opacity:0, y:20 }} animate={{ scale:1, opacity:1, y:0 }} exit={{ scale:0.95, opacity:0, y:20 }}
                        className="relative overflow-hidden rounded-[28px] p-7 max-w-[500px] w-full"
                        style={{ background:"#FFFDF8", boxShadow:"0 30px 90px rgba(0,0,0,0.22)", border:"1px solid rgba(255,255,255,0.9)" }}
                        onClick={e => e.stopPropagation()}>
                        
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-amber-950/10">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-bold"
                              style={{ background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})` }}>
                              <FiCreditCard size={18} />
                            </div>
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-wider text-[#F29A0C]">Billing Management</p>
                              <h3 className="text-[18px] font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>Manage Subscription & Billing</h3>
                            </div>
                          </div>
                          <button onClick={() => setShowManageBillingModal(false)} className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center border-none cursor-pointer text-zinc-500 hover:bg-zinc-200 transition-all">
                            <FiX size={16}/>
                          </button>
                        </div>

                        <div className="rounded-[20px] p-5 mb-4" style={{ background:"rgba(255,244,232,0.85)", border:"1.5px solid rgba(242,154,12,0.25)" }}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-black uppercase tracking-wider text-amber-700">Active Tier</span>
                            <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full text-white"
                              style={{ background: isFreeMember ? "#888" : `linear-gradient(135deg,${T.orange},${T.orange2})` }}>
                              {activeSub?.status || (isFreeMember ? "Free Member" : "Active")}
                            </span>
                          </div>
                          <h4 className="text-[20px] font-black text-zinc-900 mb-1" style={{ fontFamily:"'Poppins',sans-serif" }}>
                            {activeSub?.packageName || (isFreeMember ? "Free Trial" : "Premium Plan")}
                          </h4>
                          <p className="text-[12px] text-zinc-600">
                            {isFreeMember
                              ? "Free Account Access"
                              : `Billed ${activeSub?.amountPaid ? `₹${activeSub.amountPaid}` : "Online"} · ${remainingSubDays} days remaining`}
                          </p>
                        </div>

                        <div className="space-y-3 mb-6 bg-white p-4 rounded-[18px] border border-amber-900/10">
                          <div className="flex justify-between items-center text-[13px]">
                            <span className="text-zinc-500 font-medium">Billing Period</span>
                            <span className="font-bold text-zinc-800">
                              {activeSub?.startDate ? new Date(activeSub.startDate).toLocaleDateString("en-IN", { month: "short", day: "numeric" }) : "N/A"} - {activeSub?.endDate ? new Date(activeSub.endDate).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" }) : "Ongoing"}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-[13px] pt-2 border-t border-zinc-100">
                            <span className="text-zinc-500 font-medium">Payment Status</span>
                            <span className="font-bold text-emerald-600 flex items-center gap-1">
                              <FiCheckCircle size={13}/> {activeSub?.paymentStatus || "Completed"}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-[13px] pt-2 border-t border-zinc-100">
                            <span className="text-zinc-500 font-medium">Auto-Renewal</span>
                            <span className="font-bold text-amber-700">Enabled (Manual)</span>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2.5">
                          <button onClick={() => {
                            showToast("✓ Receipt requested. Generating digital invoice...", "success");
                          }}
                            className="w-full py-3 rounded-full text-zinc-800 bg-white border border-zinc-200 font-bold text-[12px] cursor-pointer hover:bg-zinc-50 transition-all flex items-center justify-center gap-2 shadow-sm">
                            <FiDownload size={14}/> Download Current Billing Invoice
                          </button>
                          
                          <button onClick={() => {
                            setShowManageBillingModal(false);
                            setSelectedCheckoutPkg(packageList[0] || { name:"Premium Plan", price:1499, discountedPrice:749, discountPercentage:50 });
                          }}
                            className="w-full py-3 rounded-full text-white font-bold text-[12px] cursor-pointer flex items-center justify-center gap-2 shadow-md border-none"
                            style={{ background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})` }}>
                            <FiArrowRight size={14}/> Change or Upgrade Plan
                          </button>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Subscription History Modal */}
                <AnimatePresence>
                  {showSubHistoryModal && (
                    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
                      style={{ background:"rgba(15,15,15,0.45)", backdropFilter:"blur(8px)" }}
                      onClick={() => setShowSubHistoryModal(false)}>
                      <motion.div initial={{ scale:0.95, opacity:0, y:20 }} animate={{ scale:1, opacity:1, y:0 }} exit={{ scale:0.95, opacity:0, y:20 }}
                        className="relative overflow-hidden rounded-[28px] p-7 max-w-[620px] w-full"
                        style={{ background:"#FFFDF8", boxShadow:"0 30px 90px rgba(0,0,0,0.22)", border:"1px solid rgba(255,255,255,0.9)" }}
                        onClick={e => e.stopPropagation()}>
                        
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-amber-950/10">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-bold"
                              style={{ background: `linear-gradient(135deg, ${T.olive}, ${T.olive2})` }}>
                              <FiClock size={18} />
                            </div>
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-wider text-[#6B8A5A]">Billing History</p>
                              <h3 className="text-[18px] font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>Subscription & Invoice Records</h3>
                            </div>
                          </div>
                          <button onClick={() => setShowSubHistoryModal(false)} className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center border-none cursor-pointer text-zinc-500 hover:bg-zinc-200 transition-all">
                            <FiX size={16}/>
                          </button>
                        </div>

                        <div className="max-h-[380px] overflow-y-auto pr-1 space-y-3 mb-4" style={{ scrollbarWidth: "thin" }}>
                          {((subscriptionData?.subscriptions || profile?.subscriptions || []).length > 0
                            ? (subscriptionData?.subscriptions || profile?.subscriptions)
                            : [
                                {
                                  id: activeSub?.id || 1,
                                  packageName: activeSub?.packageName || (isFreeMember ? "Free Trial" : "Premium Plan"),
                                  amountPaid: activeSub?.amountPaid || 0,
                                  paymentStatus: activeSub?.paymentStatus || "Completed",
                                  status: activeSub?.status || "Active",
                                  startDate: activeSub?.startDate || new Date().toISOString(),
                                  endDate: activeSub?.endDate || new Date(Date.now() + 30*24*60*60*1000).toISOString(),
                                }
                              ]
                          ).map((item, i) => {
                            const isItemActive = item.status === "Active";
                            return (
                              <div key={item.id || i}
                                className="rounded-[18px] p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 transition-all"
                                style={{
                                  background: isItemActive ? "rgba(255,244,232,0.85)" : "rgba(255,255,255,0.9)",
                                  border: isItemActive ? "1.5px solid rgba(242,154,12,0.3)" : "1px solid rgba(239,229,216,0.8)",
                                  boxShadow: "0 4px 14px rgba(0,0,0,0.02)"
                                }}>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-bold text-[15px] text-zinc-900" style={{ fontFamily: "'Poppins',sans-serif" }}>
                                      {item.packageName || item.tier || "Subscription Package"}
                                    </span>
                                    <span className="text-[9px] font-black px-2 py-0.5 rounded-full text-white"
                                      style={{ background: isItemActive ? `linear-gradient(135deg,${T.orange},${T.orange2})` : "#999" }}>
                                      {item.status || "Completed"}
                                    </span>
                                  </div>
                                  <p className="text-[11px] text-zinc-500">
                                    {item.startDate ? new Date(item.startDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Start"} — {item.endDate ? new Date(item.endDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "End"}
                                  </p>
                                </div>

                                <div className="flex sm:flex-col items-center sm:items-end justify-between w-full sm:w-auto gap-1">
                                  <span className="font-black text-[16px] text-zinc-900" style={{ fontFamily: "'Poppins',sans-serif" }}>
                                    ₹{(item.amountPaid || 0).toLocaleString()}
                                  </span>
                                  <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1">
                                    <FiCheckCircle size={10}/> {item.paymentStatus || "Paid"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <button onClick={() => setShowSubHistoryModal(false)}
                          className="w-full py-3 rounded-full text-zinc-700 bg-zinc-100 hover:bg-zinc-200 font-bold text-[12px] cursor-pointer transition-all border-none">
                          Close History
                        </button>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div key={toast.msg}
            initial={{ opacity:0, y:16, scale:0.95 }} animate={{ opacity:1, y:0, scale:1 }}
            exit={{ opacity:0, y:16 }}
            className="fixed bottom-7 right-7 z-[300] flex items-center gap-3 px-5 py-3.5 rounded-[16px] text-white text-[13px] font-semibold"
            style={{ background:toast.type==="error"
              ?"linear-gradient(135deg,#E24B4A,#C0392B)"
              :"linear-gradient(135deg,#1F1F1F,#2D2D2D)",
              boxShadow:"0 16px 48px rgba(0,0,0,0.25)", fontFamily:"'Inter',sans-serif" }}>
            {toast.type==="error"
              ?<FiAlertCircle size={16} style={{ color:"#FFB3B3" }}/>
              :<FiCheck size={16} style={{ color:T.olive }}/>}
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </UserDashboardLayout>
  );
}